import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient, { creatorApiClient } from '../services/apiClient';
import { dictionaryService } from '../services/dictionaryService';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { checkPronunciation, PronunciationCheckResult } from '../services/vocabularyPronunciationService';
import { learningService } from '../services/learningService';
import { convertPhonetic, getUserPhoneticSystem } from '../utils/phoneticConverter';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { isWordCorrectIgnoreCase, isPunctuationOnly, stripWordPunctuation } from '../utils/wordComparison';
import WordInputsArea from '../components/learning/WordInputsArea';
import PronunciationResultPanel from '../components/learning/PronunciationResultPanel';
import CoachingPanel from '../components/learning/CoachingPanel';
import WordPracticeModal, { WordPracticeResult } from '../components/learning/WordPracticeModal';
import './learning/DictationPage.css';
import XpToastContainer, { useXpToast } from '../components/learning/XpToast';
import LetterPicker from '../components/learning/LetterPicker';
import InputModeToggle from '../components/learning/InputModeToggle';
import { useDictationInputMode } from '../hooks/useDictationInputMode';
import { usePhonicsErrorCollector } from '../hooks/usePhonicsErrorCollector';
import { usePhonicsGuidance } from '../hooks/usePhonicsGuidance';
import PhonicsSessionSummary from '../components/learning/PhonicsSessionSummary';
import {
  LoopyVerdict,
  getCleanLocalizedDefinition,
} from '../components/learning-cards';
import './FlashcardPracticePage.css';

interface Vocabulary {
  id: string;
  word: string;
  pronunciation?: string;
  definition?: string;
  definitions?: Record<string, string>;
  examples?: { sentence: string; translation: string }[];
  audioUrl?: string;
}

interface UserVocabulary {
  id: string;
  userId: string;
  vocabularyId: string;
  masteryLevel: number;
  isFavorite: boolean;
  notes?: string;
  reviewCount: number;
  lastReviewedAt?: string;
  createdAt: string;
  vocabulary: Vocabulary;
}

// Unified practice item — works for vocabulary, knowledge point, pattern, and sentence drill items
interface PracticeItem {
  itemId: string;        // vocabularyId, knowledgePointId, patternId, or segment id
  type: 'vocabulary' | 'knowledgePoint' | 'pattern' | 'sentence';
  word: string;          // vocab word, KP term, pattern template, or full sentence text
  pronunciation?: string;
  definition?: string;
  definitions?: Record<string, string>;
  notes?: string;
  masteryLevel: number;
  // KP-only metadata: drives stage routing for concept-type categories
  kpCategory?: string;
  hasExamples?: boolean;
  // Only for vocabulary items
  userVocabulary?: UserVocabulary;
  // Sentence-only metadata: drives audio source + speaking/dictation UI
  sentenceAudioUrl?: string | null;
  sentenceTranslation?: string | null;
  sentenceCourseId?: string;
  sentenceCourseTitle?: string;
  sentenceIndex?: number;
  isErrorSentence?: boolean;
  // Authored breath-group splits surfaced as "chunk1 / chunk2 / chunk3" in
  // the speaking-stage headline once the learner has taken a first attempt.
  // Only present when the segment has speaking_strategy authored.
  sentenceChunks?: string[];
}

// Shape of items coming from CustomTab via location.state.sentences
interface SentenceDrillStateItem {
  id: string;
  courseId: string;
  courseTitle: string;
  sentenceIndex: number;
  sentenceText: string;
  audioUrl: string | null;
  translation: string | null;
  isErrorSentence: boolean;
  chunks?: string[];
}

// KP categories whose meaning is meta-linguistic (e.g. "passive voice", "conjunction").
// Asking "what does conjunction mean" against ECDICT distractors is nonsensical —
// these need example-context practice instead.
const CONCEPT_KP_CATEGORIES = new Set([
  'grammar',
  'discourse',
  'register',
  'pragmatics',
  'academic_usage',
]);

interface ReviewResult {
  vocabularyId: string;
  success: boolean;
}

// Dynamic stages for vocabulary learning
type LearningStage = 'speaking' | 'dictation' | 'meaning' | 'fillBlank';

interface WordProgress {
  [stage: string]: boolean;
}

interface MeaningQuizData {
  word: string;
  correctDefinition: string;
  distractors: string[];
  direction: 'en2native' | 'native2en';
  grammarName?: string;
  shuffledOptions?: string[];
}

interface FillBlankData {
  sentence: string;
  answers: string[];
  chunk: string;
  chunkTranslation: string;
  source: 'annotation' | 'generated';
}

const FlashcardPracticePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('vocabulary');
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Sentence drill items handed off from CustomTab via router state. Read once
  // on mount — re-renders shouldn't pull from a moving target. We keep a ref
  // so reloading the session (handleRestart) preserves the sentences.
  const sentenceDrillItemsRef = useRef<SentenceDrillStateItem[]>(
    Array.isArray((location.state as { sentences?: SentenceDrillStateItem[] })?.sentences)
      ? ((location.state as { sentences: SentenceDrillStateItem[] }).sentences)
      : [],
  );
  const [vocabularyList, setVocabularyList] = useState<UserVocabulary[]>([]);
  // Unified practice items list (vocab + KP items combined)
  const [practiceItems, setPracticeItems] = useState<PracticeItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionResults, setSessionResults] = useState<ReviewResult[]>([]);
  const [isSessionComplete, setIsSessionComplete] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const wordStartTimeRef = useRef<number>(Date.now());

  // Mastery change animation state
  const [masteryDelta, setMasteryDelta] = useState<number | null>(null);

  // XP toast (consistent with course learning modes)
  const { toasts: xpToasts, showXp } = useXpToast();

  // Keyboard height detection for mobile (prevents page jumping)
  useKeyboardHeight();

  // Dynamic stage learning state — each word is assigned ONE random stage
  const [currentStage, setCurrentStage] = useState<LearningStage>('speaking');
  const [wordStageMap, setWordStageMap] = useState<Record<string, LearningStage>>({});
  const [wordProgressMap, setWordProgressMap] = useState<Record<string, WordProgress>>({});
  // Track whether each word had errors (wrong answer / incorrect attempt)
  const [wordHadError, setWordHadError] = useState<Record<string, boolean>>({});

  // Dictation state
  const [dictationResult, setDictationResult] = useState<'correct' | 'incorrect' | null>(null);
  const [showDictationHint, setShowDictationHint] = useState(false);

  // Letter-by-letter input state (dictation always uses letter boxes)
  const [lockedLetters, setLockedLetters] = useState<boolean[]>([]); // true = locked (correct), false = input box
  const [letterInputs, setLetterInputs] = useState<string[]>([]); // current letter values
  const letterInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Pronunciation check state
  const { isRecording, audioBlob, audioUrl: recordedAudioUrl, startRecording, stopRecording, clearRecording, error: recordingError } = useAudioRecorder();
  const [isCheckingPronunciation, setIsCheckingPronunciation] = useState(false);
  const [pronunciationResult, setPronunciationResult] = useState<PronunciationCheckResult | null>(null);
  const [pronunciationError, setPronunciationError] = useState<string | null>(null);
  const [pronunciationAttempts, setPronunciationAttempts] = useState(0); // Track attempts for mastery bonus
  // Once the learner has produced a take for the current item, we switch
  // the headline sentence into "chunk1 / chunk2 / chunk3" form so they can
  // see the breath groups while reviewing the score and on subsequent
  // retries. Resets when moving to a new item (see clearAllStates).
  const [hasAttemptedSpeaking, setHasAttemptedSpeaking] = useState(false);

  // ── Word-practice modal state ────────────────────────────────
  // When the learner taps a weak word in PronunciationResultPanel,
  // the modal opens so they can rerecord just that word. The retry
  // result rewrites that word's score in pronunciationResult.words,
  // recomputes overall accuracy + composite, and (if the new score
  // is better) writes a fresh attempt row via saveShadowingResults.
  // Mirrors the long-standing flow on ShadowingPage.
  interface WordPracticeState { word: string; originalScore: number; wordIndex: number }
  const [wordPracticeMode, setWordPracticeMode] = useState<WordPracticeState | null>(null);
  const [isAssessingWord, setIsAssessingWord] = useState(false);
  const [wordPhonetic, setWordPhonetic] = useState<string | null>(null);
  const [wordAudioUrl, setWordAudioUrl] = useState<string | null>(null);
  const [isPlayingWordAudio, setIsPlayingWordAudio] = useState(false);
  const [improvedWordIndices, setImprovedWordIndices] = useState<Set<number>>(new Set());
  // Result of the most recent word-retry — drives the modal's
  // before → after view. Null means we're still in record/assess
  // state. Cleared by 「再試一次」 (back to record) or 「完成」/X
  // (close modal).
  const [wordPracticeResult, setWordPracticeResult] = useState<WordPracticeResult | null>(null);
  const wordAudioRef = useRef<HTMLAudioElement | null>(null);
  // Latest pronunciationResult inside async callbacks — without this
  // ref the closure captures the stale pre-attempt value and we'd
  // overwrite it on word-retry success.
  const pronunciationResultRef = useRef<PronunciationCheckResult | null>(null);
  useEffect(() => { pronunciationResultRef.current = pronunciationResult; }, [pronunciationResult]);

  // Audio duration thresholds (in seconds)
  const MIN_AUDIO_DURATION = 0.3;
  const MAX_AUDIO_DURATION = 10;

  // Get user's phonetic system preference
  const phoneticSystem = useMemo(() => getUserPhoneticSystem(), []);

  // Meaning quiz state
  const [meaningData, setMeaningData] = useState<Record<string, MeaningQuizData>>({});
  const [meaningSelected, setMeaningSelected] = useState<string | null>(null);
  const [meaningResult, setMeaningResult] = useState<'correct' | 'incorrect' | null>(null);

  // Fill-in-blank state
  const [fillBlankData, setFillBlankData] = useState<Record<string, FillBlankData>>({});
  const [fillBlankInputs, setFillBlankInputs] = useState<string[]>([]);
  const [fillBlankResult, setFillBlankResult] = useState<'correct' | 'incorrect' | null>(null);
  const [fillBlankLoading, setFillBlankLoading] = useState(false);

  // Practice materials loading state (prevents "unavailable" flash before API returns)
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const fillBlankInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Letter-box state for fill-blank (reuses dictation pattern)
  const [fbLetters, setFbLetters] = useState<string[][]>([]); // letters per blank word
  const [fbLockedLetters, setFbLockedLetters] = useState<boolean[][]>([]); // locked state per letter
  const [currentFbBlankIdx, setCurrentFbBlankIdx] = useState(0); // which blank we're filling

  // Success animation (reusable across all stages)
  const [showSuccessBurst, setShowSuccessBurst] = useState(false);
  // Shake animation for incorrect dictation/fillBlank attempts
  const [shakeBoxes, setShakeBoxes] = useState(false);
  const { isLetterPicker, toggle: toggleInputMode } = useDictationInputMode();
  const { addErrors: addPhonicsErrors } = usePhonicsErrorCollector(undefined, 'flashcard');
  const { handleLetterError, getSessionErrors } = usePhonicsGuidance();
  const [letterPickerResetKey, setLetterPickerResetKey] = useState(0);
  // Track whether LetterPicker had errors/hints for mastery calculation
  const lpHadErrorsRef = useRef(false);
  const handleLpErrorData = useCallback((data: import('../components/learning/LetterPicker').LetterPickerErrorData) => {
    if (data.errors.length > 0 || data.revealedPositions.length > 0) {
      lpHadErrorsRef.current = true;
    }
    addPhonicsErrors(data);
  }, [addPhonicsErrors]);

  // Get filter from URL params
  const practiceMode = searchParams.get('mode') || 'smart'; // smart, weak, custom
  const practiceCount = parseInt(searchParams.get('count') || '20', 10);
  const practiceLevels = searchParams.get('levels')?.split(',').map(Number) || [0, 1, 2, 3, 4, 5];
  const filterFavorite = searchParams.get('favorite') === 'true';

  // Daily review mode: specific vocab/KP/pattern IDs from Learning Journey Dashboard
  const urlVocabIds = useMemo(() => searchParams.get('vocabIds')?.split(',').filter(Boolean) || [], [searchParams]);
  const urlKpIds = useMemo(() => searchParams.get('kpIds')?.split(',').filter(Boolean) || [], [searchParams]);
  const urlPatternIds = useMemo(() => searchParams.get('patternIds')?.split(',').filter(Boolean) || [], [searchParams]);
  // Sentence drill counts as a curated session too — without it, a
  // sentence-only entry from Custom Review would fall through to
  // selectVocabulary() and dump the entire vocab book into the queue.
  const hasSentenceDrill = sentenceDrillItemsRef.current.length > 0;
  const isDailyReview = urlVocabIds.length > 0 || urlKpIds.length > 0 || urlPatternIds.length > 0 || hasSentenceDrill;

  // Parse active stages from URL (default: speaking + dictation for backward compat)
  const activeStages = useMemo<LearningStage[]>(() => {
    const raw = searchParams.get('stages');
    if (!raw) return ['speaking', 'dictation'];
    const valid: LearningStage[] = [];
    for (const s of raw.split(',')) {
      if (['speaking', 'dictation', 'meaning', 'fillBlank'].includes(s)) {
        valid.push(s as LearningStage);
      }
    }
    return valid.length > 0 ? valid : ['speaking', 'dictation'];
  }, [searchParams]);

  // Active items list: practiceItems when populated (daily review), else built from vocabularyList
  const activeItems: PracticeItem[] = useMemo(() => {
    if (practiceItems.length > 0) return practiceItems;
    return vocabularyList.map(uv => ({
      itemId: uv.vocabularyId,
      type: 'vocabulary' as const,
      word: uv.vocabulary.word,
      pronunciation: uv.vocabulary.pronunciation,
      definition: uv.vocabulary.definition,
      definitions: uv.vocabulary.definitions,
      notes: uv.notes,
      masteryLevel: uv.masteryLevel,
      userVocabulary: uv,
    }));
  }, [practiceItems, vocabularyList]);

  // Current practice item
  const currentPracticeItem: PracticeItem | null = activeItems[currentIndex] || null;

  // Load vocabulary
  useEffect(() => {
    loadVocabulary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Smart selection: prioritize low mastery words, limit session size
  const selectVocabulary = (items: UserVocabulary[]): UserVocabulary[] => {
    if (items.length === 0) return [];

    let filtered = [...items];
    const maxCount = practiceCount === 999 ? items.length : practiceCount;

    // Apply favorites filter
    if (filterFavorite) {
      filtered = filtered.filter(v => v.isFavorite);
    }

    // Apply mode-specific filtering
    if (practiceMode === 'weak') {
      filtered = filtered.filter(v => v.masteryLevel <= 2);
    } else if (practiceMode === 'custom') {
      filtered = filtered.filter(v => practiceLevels.includes(v.masteryLevel));
    }

    // Group by mastery level
    const byMastery: Record<number, UserVocabulary[]> = {};
    filtered.forEach(item => {
      const level = item.masteryLevel;
      if (!byMastery[level]) byMastery[level] = [];
      byMastery[level].push(item);
    });

    // Shuffle within each group
    Object.keys(byMastery).forEach(level => {
      byMastery[Number(level)] = byMastery[Number(level)].sort(() => Math.random() - 0.5);
    });

    // Build result: prioritize lower mastery levels
    const result: UserVocabulary[] = [];
    const levels = [0, 1, 2, 3, 4, 5];

    for (const level of levels) {
      if (byMastery[level]) {
        for (const item of byMastery[level]) {
          if (result.length >= maxCount) break;
          result.push(item);
        }
      }
      if (result.length >= maxCount) break;
    }

    // Final shuffle to mix levels slightly (but keep low mastery items)
    // Only shuffle if we have items from multiple levels
    const uniqueLevels = new Set(result.map(r => r.masteryLevel));
    if (uniqueLevels.size > 1) {
      // Light shuffle: swap some adjacent items
      for (let i = 0; i < result.length - 1; i++) {
        if (Math.random() < 0.3) {
          [result[i], result[i + 1]] = [result[i + 1], result[i]];
        }
      }
    }

    return result;
  };

  const loadVocabulary = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError(t('practice.loginRequired'));
        setLoading(false);
        return;
      }

      // Load all vocabulary, filtering is done client-side
      const response = await apiClient.get('/vocabulary-book/me');
      const items = response.data.data || [];

      let selected: UserVocabulary[];
      if (isDailyReview && urlVocabIds.length > 0) {
        // Daily review mode: filter to specific vocab IDs, respect count limit
        const idSet = new Set(urlVocabIds);
        selected = items.filter((v: UserVocabulary) => idSet.has(v.vocabularyId));

        // Custom Review may pass vocab IDs the learner has never practiced
        // (includeUnpracticed=true). Those rows aren't in /vocabulary-book/me,
        // so fetch them directly from the public vocabulary endpoint and wrap
        // them as a synthetic UserVocabulary with zero FSRS state — the review
        // POST will materialize a real row on first answer.
        const haveIds = new Set(selected.map(v => v.vocabularyId));
        const missingIds = urlVocabIds.filter(id => !haveIds.has(id));
        if (missingIds.length > 0) {
          const fetched = await Promise.all(
            missingIds.map(id =>
              apiClient.get(`/vocabulary/${id}`).catch(() => null),
            ),
          );
          for (const res of fetched) {
            const v = res?.data?.data;
            if (!v) continue;
            selected.push({
              id: `synthetic-${v.id}`,
              vocabularyId: v.id,
              vocabulary: v,
              masteryLevel: 0,
              reviewCount: 0,
              lastReviewedAt: null,
              due: new Date(),
              stability: 0,
              difficulty: 0,
              elapsedDays: 0,
              scheduledDays: 0,
              reps: 0,
              lapses: 0,
              state: 0,
              isFavorite: false,
              notes: '',
            } as unknown as UserVocabulary);
          }
        }

        // Apply count limit (practiceCount from URL or default)
        const maxCount = practiceCount === 999 ? selected.length : practiceCount;
        if (selected.length > maxCount) {
          selected = selected.slice(0, maxCount);
        }
      } else if (isDailyReview && urlVocabIds.length === 0) {
        // Only KP items from daily review, no vocab
        selected = [];
      } else {
        // Normal mode: apply selection based on practice options
        selected = selectVocabulary(items);
      }
      setVocabularyList(selected);

      // Build unified practice items from vocab
      const vocabPracticeItems: PracticeItem[] = selected.map(uv => ({
        itemId: uv.vocabularyId,
        type: 'vocabulary' as const,
        word: uv.vocabulary.word,
        pronunciation: uv.vocabulary.pronunciation,
        definition: uv.vocabulary.definition,
        definitions: uv.vocabulary.definitions,
        notes: uv.notes,
        masteryLevel: uv.masteryLevel,
        userVocabulary: uv,
      }));

      // Load KP items if daily review includes them
      let kpPracticeItems: PracticeItem[] = [];
      if (isDailyReview && urlKpIds.length > 0) {
        kpPracticeItems = await loadKpItems(urlKpIds);
      }

      // Load pattern items if daily review includes them
      let patternPracticeItems: PracticeItem[] = [];
      if (isDailyReview && urlPatternIds.length > 0) {
        patternPracticeItems = await loadPatternItems(urlPatternIds);
      }

      // Drop concept-type KPs that have no example sentence — there's no
      // honest way to quiz "what does <grammar concept> mean" with the data
      // we have. Filtered ones simply don't enter the session.
      const usableKpItems = kpPracticeItems.filter(item => {
        if (!item.kpCategory || !CONCEPT_KP_CATEGORIES.has(item.kpCategory)) return true;
        return !!item.hasExamples;
      });

      // Sentence drill items (handed off from Custom Review). Each becomes
      // its own PracticeItem; the existing speaking/dictation flow is
      // re-targeted at sentence-level when these are present.
      const sentencePracticeItems: PracticeItem[] = sentenceDrillItemsRef.current.map((s) => ({
        itemId: s.id,
        type: 'sentence' as const,
        word: s.sentenceText,
        masteryLevel: 0,
        sentenceAudioUrl: s.audioUrl,
        sentenceTranslation: s.translation,
        sentenceCourseId: s.courseId,
        sentenceCourseTitle: s.courseTitle,
        sentenceIndex: s.sentenceIndex,
        isErrorSentence: s.isErrorSentence,
        sentenceChunks: Array.isArray(s.chunks) ? s.chunks : [],
      }));
      // Sentence drill items lead the queue when present — this is the
      // headline new affordance for Custom Review (聽寫整句 / 跟讀整句),
      // and learners selecting those stages expect to hit sentences
      // before single-word cards. Backend already ranked them by error
      // priority + complexity, so the natural lead is "your worst
      // sentences first."
      const vocabBlock = [...vocabPracticeItems, ...usableKpItems, ...patternPracticeItems];
      // Light shuffle within the vocab/KP/pattern block to mix types
      for (let i = vocabBlock.length - 1; i > 0; i--) {
        if (Math.random() < 0.3) {
          [vocabBlock[i], vocabBlock[i - 1]] = [vocabBlock[i - 1], vocabBlock[i]];
        }
      }
      const allItems = [...sentencePracticeItems, ...vocabBlock];
      setPracticeItems(allItems);

      // Assign each item ONE random stage from activeStages.
      // - KP/pattern items can only get meaning or fillBlank.
      // - Concept-type KPs (grammar, discourse, register, pragmatics, academic_usage)
      //   force fillBlank — meaning quiz against ECDICT distractors is meaningless
      //   for meta-linguistic terms.
      const quizOnlyStages: LearningStage[] = activeStages.filter(s => s === 'meaning' || s === 'fillBlank');
      const speechOnlyStages: LearningStage[] = activeStages.filter(s => s === 'speaking' || s === 'dictation');
      const hasSentenceItems = sentencePracticeItems.length > 0;
      const stageMap: Record<string, LearningStage> = {};
      for (const item of allItems) {
        if (item.type === 'sentence') {
          // Sentence items are speech-only — meaning/fillBlank don't apply.
          // If the user happened to deselect both speaking and dictation,
          // skip the item entirely (auto-skip effect catches this).
          stageMap[item.itemId] = speechOnlyStages.length > 0
            ? speechOnlyStages[Math.floor(Math.random() * speechOnlyStages.length)]
            : 'speaking';
        } else if (item.type === 'knowledgePoint' && item.kpCategory && CONCEPT_KP_CATEGORIES.has(item.kpCategory)) {
          stageMap[item.itemId] = activeStages.includes('fillBlank') ? 'fillBlank' : 'meaning';
        } else if (item.type === 'knowledgePoint' || item.type === 'pattern') {
          stageMap[item.itemId] = quizOnlyStages.length > 0
            ? quizOnlyStages[Math.floor(Math.random() * quizOnlyStages.length)]
            : 'meaning'; // fallback
        } else {
          // Vocab items: when sentence drill is in play, sentences cover
          // the speaking/dictation surface — don't double up by sending
          // single words to those stages too. Falls back to all active
          // stages only when sentences aren't in this session.
          const vocabPool = hasSentenceItems && quizOnlyStages.length > 0
            ? quizOnlyStages
            : activeStages;
          stageMap[item.itemId] = vocabPool[Math.floor(Math.random() * vocabPool.length)];
        }
      }
      setWordStageMap(stageMap);
      // Set initial stage for first item
      if (allItems.length > 0) {
        setCurrentStage(stageMap[allItems[0].itemId]);
      }

      if (allItems.length === 0) {
        if (filterFavorite) {
          setError(t('practice.noFavoritesMatch'));
        } else if (practiceMode === 'weak') {
          setError(t('practice.noWeakWords'));
        } else {
          setError(t('practice.emptyVocabulary'));
        }
      } else {
        // Fetch practice materials for meaning/fillBlank stages (non-blocking)
        const needsModes: string[] = [];
        if (activeStages.includes('meaning')) needsModes.push('meaning');
        if (activeStages.includes('fillBlank')) needsModes.push('fillBlank');

        if (needsModes.length > 0) {
          setMaterialsLoading(true);
          // Fetch vocab and KP materials in parallel
          const promises: Promise<void>[] = [];
          if (selected.length > 0) {
            promises.push(fetchPracticeMaterials(selected, needsModes));
          }
          if (urlKpIds.length > 0) {
            promises.push(fetchKpPracticeMaterials(urlKpIds, needsModes));
          }
          if (urlPatternIds.length > 0) {
            promises.push(fetchPatternPracticeMaterials(urlPatternIds, needsModes));
          }
          if (promises.length > 0) {
            Promise.all(promises).finally(() => setMaterialsLoading(false));
          } else {
            setMaterialsLoading(false);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load vocabulary:', err);
      setError(t('practice.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Load KP items from CEFR knowledge endpoint for daily review
  const loadKpItems = async (kpIds: string[]): Promise<PracticeItem[]> => {
    const items: PracticeItem[] = [];
    const locale = localStorage.getItem('i18nextLng') || 'zh-TW';
    try {
      // Fetch each KP's data (they're small, parallel is fine)
      const results = await Promise.all(
        kpIds.map(id => apiClient.get(`/cefr-knowledge/${id}`).catch(() => null))
      );
      for (const res of results) {
        if (!res?.data) continue;
        const kp = res.data;
        const hasCJK = (s: string) => /[\u4e00-\u9fff\u3400-\u4dbf]/.test(s);
        let def = kp.definitions?.[locale];
        if (def && locale.startsWith('zh') && !hasCJK(def)) {
          def = kp.definitions?.['zh-CN'] || kp.definitions?.['zh-TW'] || def;
        }
        if (!def && locale === 'zh-TW') def = kp.definitions?.['zh-CN'];
        if (!def && locale === 'zh-CN') def = kp.definitions?.['zh-TW'];
        if (!def) def = kp.definitions?.en;

        const examples = kp.examples || {};
        const hasExamples = Object.values(examples).some(arr => Array.isArray(arr) && arr.length > 0);
        items.push({
          itemId: kp.id,
          type: 'knowledgePoint',
          word: kp.term,
          definition: def || kp.term,
          definitions: kp.definitions,
          masteryLevel: 0,
          kpCategory: kp.category,
          hasExamples,
        });
      }
    } catch (err) {
      console.error('Failed to load KP items:', err);
    }
    return items;
  };

  // Load sentence pattern items for daily review
  const loadPatternItems = async (patternIds: string[]): Promise<PracticeItem[]> => {
    const items: PracticeItem[] = [];
    try {
      const results = await Promise.all(
        patternIds.map(id => creatorApiClient.get(`/learning/patterns/${id}`).catch(() => {
          // Pattern doesn't have a dedicated GET endpoint, so we get info from practice materials
          return null;
        }))
      );
      // If individual GET fails, just create items from IDs and let practice materials fill the data
      for (const pid of patternIds) {
        items.push({
          itemId: pid,
          type: 'pattern',
          word: '', // Will be filled by practice materials
          definition: '',
          masteryLevel: 0,
        });
      }
    } catch (err) {
      console.error('Failed to load pattern items:', err);
      // Fallback: create stub items
      for (const pid of patternIds) {
        items.push({
          itemId: pid,
          type: 'pattern',
          word: '',
          definition: '',
          masteryLevel: 0,
        });
      }
    }
    return items;
  };

  // Fetch meaning quiz and fill-blank materials from backend
  const fetchPracticeMaterials = async (items: UserVocabulary[], modes: string[]) => {
    try {
      const locale = localStorage.getItem('i18nextLng') || 'zh-TW';
      const vocabIds = items.map(v => v.vocabularyId);
      const response = await creatorApiClient.post('/learning/vocabulary/practice-materials', {
        vocabularyIds: vocabIds,
        modes,
        locale,
      });

      processPracticeMaterialsResponse(response.data, items.map(v => v.vocabulary.word));
    } catch (err) {
      console.error('Failed to fetch practice materials:', err);
    }
  };

  // Fetch KP practice materials from backend
  const fetchKpPracticeMaterials = async (kpIds: string[], modes: string[]) => {
    try {
      const locale = localStorage.getItem('i18nextLng') || 'zh-TW';
      const response = await creatorApiClient.post('/learning/knowledge-points/practice-materials', {
        knowledgePointIds: kpIds,
        modes,
        locale,
      });

      // Build word pool from KP terms for native2en distractors
      const kpWordPool = Object.values(response.data as Record<string, any>)
        .filter((m: any) => m.meaning?.word)
        .map((m: any) => m.meaning.word);
      processPracticeMaterialsResponse(response.data, kpWordPool);
    } catch (err) {
      console.error('Failed to fetch KP practice materials:', err);
    }
  };

  // Fetch pattern practice materials from backend
  const fetchPatternPracticeMaterials = async (patternIds: string[], modes: string[]) => {
    try {
      const locale = localStorage.getItem('i18nextLng') || 'zh-TW';
      const response = await creatorApiClient.post('/learning/patterns/practice-materials', {
        patternIds,
        modes,
        locale,
      });

      // Update pattern PracticeItems with word/definition from materials
      setPracticeItems(prev => prev.map(item => {
        if (item.type !== 'pattern') return item;
        const mat = (response.data as Record<string, any>)?.[item.itemId];
        if (mat?.meaning) {
          return {
            ...item,
            word: mat.meaning.word || item.word,
            definition: mat.meaning.correctDefinition || item.definition,
          };
        }
        return item;
      }));

      const patternWordPool = Object.values(response.data as Record<string, any>)
        .filter((m: any) => m.meaning?.word)
        .map((m: any) => m.meaning.word);
      processPracticeMaterialsResponse(response.data, patternWordPool);
    } catch (err) {
      console.error('Failed to fetch pattern practice materials:', err);
    }
  };

  // Process practice materials response (shared between vocab and KP)
  const processPracticeMaterialsResponse = (data: Record<string, any>, wordPool: string[]) => {
    const newMeaningData: Record<string, MeaningQuizData> = {};
    const newFillBlankData: Record<string, FillBlankData> = {};

    for (const [itemId, materials] of Object.entries(data) as [string, any][]) {
      if (materials.meaning) {
        const m = materials.meaning;
        let allOptions: string[];

        if (m.direction === 'native2en') {
          const wordDistractors = wordPool
            .filter(w => w !== m.word)
            .sort(() => Math.random() - 0.5)
            .slice(0, 3);
          allOptions = [m.word, ...wordDistractors];
        } else {
          allOptions = [m.correctDefinition, ...m.distractors];
        }
        // Shuffle
        for (let i = allOptions.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [allOptions[i], allOptions[j]] = [allOptions[j], allOptions[i]];
        }
        newMeaningData[itemId] = { ...m, shuffledOptions: allOptions };
      }
      if (materials.fillBlank) {
        const fb = materials.fillBlank;
        const splitAnswers: string[] = [];
        let splitSentence = fb.sentence;
        for (const answer of fb.answers) {
          if (answer.includes(' ')) {
            const words = answer.split(/\s+/);
            splitAnswers.push(...words);
            splitSentence = splitSentence.replace('_____', words.map(() => '_____').join(' '));
          } else {
            splitAnswers.push(answer);
          }
        }
        newFillBlankData[itemId] = {
          ...fb,
          sentence: splitSentence,
          answers: splitAnswers,
        };
      }
    }

    // Merge into existing state (important: don't overwrite, merge)
    setMeaningData(prev => ({ ...prev, ...newMeaningData }));
    setFillBlankData(prev => ({ ...prev, ...newFillBlankData }));
  };

  // Auto-check pronunciation when recording stops (if duration is reasonable).
  // The same recorder serves two flows: sentence-level take (handlePronunciationCheck)
  // and word-level retry from the WordPracticeModal (handleAssessWord). We branch
  // on `wordPracticeMode` so the audio blob lands in the right assessor.
  useEffect(() => {
    if (!audioBlob || !recordedAudioUrl || isRecording || activeItems.length === 0) return;

    // Check audio duration
    const audio = new Audio(recordedAudioUrl);
    audio.addEventListener('loadedmetadata', () => {
      const duration = audio.duration;

      if (duration < MIN_AUDIO_DURATION) {
        setPronunciationError(t('practice.recordingTooShort'));
        clearRecording();
      } else if (duration > MAX_AUDIO_DURATION) {
        setPronunciationError(t('practice.recordingTooLong'));
        clearRecording();
      } else if (wordPracticeMode) {
        // Word-retry path — assess just the single word.
        handleAssessWord(audioBlob);
      } else {
        // Sentence-level path.
        handlePronunciationCheck(audioBlob);
      }
    });

    audio.addEventListener('error', () => {
      // If can't determine duration, proceed with check anyway
      if (wordPracticeMode) {
        handleAssessWord(audioBlob);
      } else {
        handlePronunciationCheck(audioBlob);
      }
    });

    // Cleanup
    return () => {
      audio.pause();
      audio.src = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioBlob, recordedAudioUrl, isRecording, wordPracticeMode]);

  // Focus first empty input when locked letters change (after incorrect attempt)
  useEffect(() => {
    if (currentStage === 'dictation' && lockedLetters.length > 0 && lockedLetters.some(l => l)) {
      const firstEmptyIndex = lockedLetters.findIndex(l => !l);
      if (firstEmptyIndex >= 0) {
        setTimeout(() => {
          letterInputRefs.current[firstEmptyIndex]?.focus();
        }, 50);
      }
    }
  }, [currentStage, lockedLetters]);

  // Sentence-level dictation: per-word inputs that mirror the existing
  // course-level DictationPage UX (.word-inputs-container / .word-input
  // styles imported from DictationPage.css). One input per word, Tab /
  // Space advances, Enter submits. Letter-by-letter would explode for a
  // 20-word sentence; a single textarea didn't match the established
  // dictation interface learners are used to.
  const [sentenceWordInputs, setSentenceWordInputs] = useState<string[]>([]);
  const [sentenceActiveWordIdx, setSentenceActiveWordIdx] = useState(0);
  const [sentenceDictationResult, setSentenceDictationResult] = useState<'correct' | 'incorrect' | null>(null);
  const sentenceWordRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Split sentence into words (mirrors DictationPage.correctWords).
  // Punctuation-only tokens render as static text — typing into "…" or
  // "—" makes no sense. Hyphenated words like "five-minute" are split
  // into ["five-", "minute"] so the learner only types the alphabetic
  // halves; the dash stays glued to the preceding token as static text
  // (same convention course-level DictationPage uses).
  const sentenceWords = useMemo(() => {
    if (currentPracticeItem?.type !== 'sentence' || !currentPracticeItem.word) return [];
    const raw = currentPracticeItem.word.split(/\s+/).filter(w => w.length > 0);
    const expanded: string[] = [];
    for (const word of raw) {
      if (word.includes('-') && !word.startsWith('-') && !word.endsWith('-')) {
        const parts = word.split('-');
        parts.forEach((part, idx) => {
          expanded.push(idx < parts.length - 1 ? part + '-' : part);
        });
      } else {
        expanded.push(word);
      }
    }
    return expanded;
  }, [currentPracticeItem]);

  // Initialize dictation letter boxes when entering dictation stage
  // Pre-fill and lock non-letter characters (spaces, hyphens, apostrophes)
  // Also auto-play the word so the user knows what to type
  //
  // Sentence drill items use one input per word (see sentence dictation
  // block in render) — letter-box init would explode them into 100+
  // inputs and word-level dictation already has a per-word UX learners
  // know from the course-level DictationPage.
  useEffect(() => {
    if (currentStage !== 'dictation' || !currentPracticeItem) return;
    if (currentPracticeItem.type === 'sentence') {
      // Only init + auto-play on the FIRST fire after a sentence change —
      // re-firing because of the setSentenceWordInputs we just performed
      // would call audio.pause() mid-playback and leave the icon flipping.
      if (sentenceWords.length > 0 && sentenceWordInputs.length !== sentenceWords.length) {
        setSentenceWordInputs(new Array(sentenceWords.length).fill(''));
        const firstInputable = sentenceWords.findIndex(w => /[a-zA-Z0-9]/.test(w));
        setSentenceActiveWordIdx(firstInputable >= 0 ? firstInputable : 0);
        sentenceWordRefs.current = new Array(sentenceWords.length).fill(null);
        playPronunciation(currentPracticeItem.word);
      }
      return;
    }
    const word = currentPracticeItem.word;
    if (letterInputs.length !== word.length) {
      const inputs = word.split('').map(ch => /[a-zA-Z]/.test(ch) ? '' : ch);
      const locked = word.split('').map(ch => !/[a-zA-Z]/.test(ch));
      setLetterInputs(inputs);
      setLockedLetters(locked);
      // Auto-play pronunciation when entering dictation stage
      playPronunciation(word);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStage, currentIndex, currentPracticeItem, letterInputs.length, sentenceWords.length, sentenceWordInputs.length]);

  // Focus the first inputable word when entering sentence dictation. Doing
  // this in a separate effect (rather than in the init block above) lets
  // React commit the inputs first, so the ref array is already populated.
  useEffect(() => {
    if (currentStage !== 'dictation' || currentPracticeItem?.type !== 'sentence') return;
    const focusEl = sentenceWordRefs.current[sentenceActiveWordIdx];
    if (focusEl && document.activeElement !== focusEl) {
      // Defer focus a tick — React's commit happens synchronously but
      // the input element may briefly be detached during a sentence swap.
      setTimeout(() => focusEl?.focus(), 50);
    }
  }, [currentStage, currentPracticeItem, sentenceActiveWordIdx, sentenceWordInputs.length]);

  // Initialize fill-blank letter boxes when entering fillBlank stage
  useEffect(() => {
    if (currentStage !== 'fillBlank' || !currentPracticeItem) return;
    const fb = fillBlankData[currentPracticeItem.itemId];
    if (fb && fbLetters.length === 0 && fb.answers.length > 0) {
      setFillBlankInputs(new Array(fb.answers.length).fill(''));
      setFbLetters(fb.answers.map(a => new Array(a.length).fill('')));
      setFbLockedLetters(fb.answers.map(a => new Array(a.length).fill(false)));
    }
  }, [currentStage, currentIndex, currentPracticeItem, fillBlankData, fbLetters.length]);

  // Handle pronunciation check
  const handlePronunciationCheck = async (blob: Blob) => {
    if (!blob || !currentPracticeItem) return;

    setIsCheckingPronunciation(true);
    setPronunciationError(null);
    // Compute next attempt locally — backend uses this to pick a stricter
    // threshold on first take (≥80) and a lenient one on retry (≥60).
    const nextAttempt = pronunciationAttempts + 1;
    setPronunciationAttempts(nextAttempt);

    try {
      const response = await checkPronunciation(
        blob,
        currentPracticeItem.word,
        nextAttempt,
        undefined, // locale handled inside via getUserLocale()
        coach => {
          // Progressive UI: when the background /coach call resolves,
          // splice the result into pronunciationResult so the
          // <CoachingPanel> renders without blocking the score panel.
          setPronunciationResult(prev => prev ? { ...prev, coach } : prev);
        },
      );
      if (response.success && response.data) {
        setPronunciationResult(response.data);
        setHasAttemptedSpeaking(true);

        // Phase 2 persistence — sentence-drill items have the
        // (courseId, sentenceIndex) context required to write a
        // shadowing_attempts row, including the per-word breakdown
        // that powers the learning analyzer + future weakness queries.
        // Best-effort: never block the UI on the save.
        const pItem = currentPracticeItem;
        if (pItem.type === 'sentence' && pItem.sentenceCourseId && typeof pItem.sentenceIndex === 'number') {
          learningService.saveShadowingResults({
            courseId: pItem.sentenceCourseId,
            sentenceIndex: pItem.sentenceIndex,
            sentenceText: pItem.word,
            mode: 'shadowing',
            scriptVisible: true,
            overallScore: response.data.overallScore ?? 0,
            accuracyScore: response.data.accuracyScore ?? 0,
            fluencyScore: response.data.fluencyScore ?? 0,
            completenessScore: response.data.completenessScore ?? 0,
            prosodyScore: response.data.prosodyScore,
            recognizedText: response.data.transcribedText,
            words: (response.data.words ?? []).map(w => ({
              word: w.word,
              accuracyScore: w.accuracyScore,
              errorType: w.errorType,
              phonemes: w.phonemes,
            })),
          }).catch(err => {
            // eslint-disable-next-line no-console
            console.warn('[speaking] saveShadowingResults (sentence) failed', err);
          });
        }

        if (response.data.isCorrect) {
          triggerSuccessBurst();
          const delta = nextAttempt <= 1 ? 2 : 1;
          setMasteryDelta(delta);
          setTimeout(() => setMasteryDelta(null), 1400);
        } else {
          setWordHadError(prev => ({ ...prev, [currentPracticeItem.itemId]: true }));
        }
      } else {
        setPronunciationError(response.error || t('practice.pronunciationCheckFailed'));
      }
    } catch (err) {
      console.error('Pronunciation check error:', err);
      setPronunciationError(t('practice.pronunciationCheckFailedRetry'));
    } finally {
      setIsCheckingPronunciation(false);
      clearRecording();
    }
  };

  // Clear pronunciation result when retrying (don't reset attempts)
  const clearPronunciationState = () => {
    setPronunciationResult(null);
    setPronunciationError(null);
    clearRecording();
  };

  // Reset pronunciation attempts (called when moving to next word)
  const resetPronunciationAttempts = () => {
    setPronunciationAttempts(0);
  };

  // ── Word-practice handlers ────────────────────────────────────────
  // The PronunciationResultPanel reports a click on a weak word; we
  // open the modal, fetch the dictionary phonetic + audio for context,
  // and let the recorder collect the new take. The audioBlob effect
  // above branches into handleAssessWord because wordPracticeMode is
  // truthy. Mirrors the long-standing flow on ShadowingPage so both
  // surfaces feel identical.
  const handleWordPractice = useCallback(async (word: string, wordIndex: number) => {
    clearRecording();
    // The diff tokenizer keeps trailing punctuation; "ten." / "anecdotes."
    // need to be cleaned before we (a) show the headline in the modal,
    // (b) look up the dictionary entry, and (c) hand the reference text
    // to the per-word ASR. Falls back to the raw token if stripping
    // would produce an empty string (defensive — wouldn't happen for
    // real lexical words, but pure-punctuation tokens already get
    // filtered out by the panel's renderableWords).
    const clean = stripWordPunctuation(word) || word;
    const original = pronunciationResultRef.current?.words?.[wordIndex]?.accuracyScore ?? 0;
    setWordPracticeMode({ word: clean, originalScore: original, wordIndex });
    setWordPhonetic(null);
    setWordAudioUrl(null);
    setIsPlayingWordAudio(false);

    // Dictionary phonetic + audio is best-effort; absence isn't an error.
    try {
      const dictResult = await dictionaryService.lookup(clean, { generateAudio: true });
      if (dictResult.phonetic) setWordPhonetic(dictResult.phonetic);
      if (dictResult.audio && dictResult.audio.length > 0) {
        setWordAudioUrl(dictResult.audio[0].url);
      }
    } catch (err) {
      // Silent — UI degrades gracefully without phonetic / audio.
      void err;
    }
  }, [clearRecording]);

  const handlePlayWordAudio = useCallback(() => {
    if (!wordAudioUrl) return;
    const el = wordAudioRef.current ?? new Audio();
    wordAudioRef.current = el;
    if (isPlayingWordAudio) {
      el.pause();
      el.currentTime = 0;
      setIsPlayingWordAudio(false);
      return;
    }
    el.src = wordAudioUrl;
    el.onended = () => setIsPlayingWordAudio(false);
    el.onerror = () => setIsPlayingWordAudio(false);
    el.play().then(() => setIsPlayingWordAudio(true)).catch(() => setIsPlayingWordAudio(false));
  }, [wordAudioUrl, isPlayingWordAudio]);

  const handleCancelWordPractice = useCallback(() => {
    setWordPracticeMode(null);
    setWordPhonetic(null);
    setWordAudioUrl(null);
    setIsPlayingWordAudio(false);
    setWordPracticeResult(null);
    clearRecording();
  }, [clearRecording]);

  // 「再試一次」 inside the modal — clear the result view, hand the
  // mic back to the learner without dismissing the modal.
  const handleRetryWordPractice = useCallback(() => {
    setWordPracticeResult(null);
    clearRecording();
  }, [clearRecording]);

  // Recompute the sentence-level overall score after one word's
  // accuracy changes. Matches the v2 backend's synthesise() weighting
  // (0.4 / 0.2 / 0.2 / 0.2) so the recomputed value is consistent
  // with what a fresh sentence-level take would have produced.
  const recomputeOverall = (
    accuracyAvg: number,
    fluency: number | undefined,
    completeness: number | undefined,
    prosody: number | undefined,
  ): number => {
    return Math.round(
      0.4 * accuracyAvg
      + 0.2 * (fluency ?? 0)
      + 0.2 * (completeness ?? 0)
      + 0.2 * (prosody ?? 0),
    );
  };

  const handleAssessWord = async (blob: Blob) => {
    const mode = wordPracticeMode;
    const baseline = pronunciationResultRef.current;
    if (!mode || !baseline) {
      clearRecording();
      return;
    }
    setIsAssessingWord(true);
    try {
      const response = await checkPronunciation(blob, mode.word);
      if (!response.success || !response.data) {
        // Stay in the modal so the learner can retry; surface the
        // error inline through the existing pronunciationError slot.
        setPronunciationError(response.error || t('practice.pronunciationCheckFailed'));
        return;
      }
      const wordScore = response.data.words?.[0]?.accuracyScore
        ?? response.data.accuracyScore
        ?? 0;

      // Splice the new score into the baseline result's words[] and
      // recompute the headline accuracy / overall.
      const oldWords = baseline.words ?? [];
      const wordBefore = oldWords[mode.wordIndex]?.accuracyScore ?? mode.originalScore ?? 0;
      const newWords = oldWords.map((w, i) =>
        i === mode.wordIndex ? { ...w, accuracyScore: wordScore, errorType: 'None' } : w,
      );
      const avgAccuracy = newWords.length > 0
        ? newWords.reduce((s, w) => s + (w.accuracyScore ?? 0), 0) / newWords.length
        : wordScore;
      const newOverall = recomputeOverall(
        avgAccuracy,
        baseline.fluencyScore,
        baseline.completenessScore,
        baseline.prosodyScore,
      );
      const updated: PronunciationCheckResult = {
        ...baseline,
        accuracyScore: Math.round(avgAccuracy),
        overallScore: newOverall,
        words: newWords,
      };
      setPronunciationResult(updated);
      setImprovedWordIndices(prev => {
        const next = new Set(prev);
        next.add(mode.wordIndex);
        return next;
      });
      // Hand the modal a before → after snapshot so it shows the
      // deltas instead of auto-closing. Learner sees their progress
      // and chooses 「再試一次」 (retry without closing) or 「完成」.
      setWordPracticeResult({
        wordBefore: Math.round(wordBefore),
        wordAfter: Math.round(wordScore),
        accuracyBefore: Math.round(baseline.accuracyScore ?? 0),
        accuracyAfter: Math.round(avgAccuracy),
        overallBefore: Math.round(baseline.overallScore ?? 0),
        overallAfter: newOverall,
      });

      // Phase 2 persistence — save a fresh shadowing_attempts row so
      // the per-word retry shows up in `word_scores` history. Only
      // sentence-drill items have the (courseId, sentenceIndex)
      // context required by the endpoint.
      const pItem = currentPracticeItem;
      if (pItem?.type === 'sentence' && pItem.sentenceCourseId && typeof pItem.sentenceIndex === 'number') {
        learningService.saveShadowingResults({
          courseId: pItem.sentenceCourseId,
          sentenceIndex: pItem.sentenceIndex,
          sentenceText: pItem.word,
          mode: 'shadowing',
          scriptVisible: true,
          overallScore: newOverall,
          accuracyScore: Math.round(avgAccuracy),
          fluencyScore: baseline.fluencyScore ?? 0,
          completenessScore: baseline.completenessScore ?? 0,
          prosodyScore: baseline.prosodyScore,
          recognizedText: updated.transcribedText,
          words: newWords.map(w => ({
            word: w.word,
            accuracyScore: w.accuracyScore,
            errorType: w.errorType,
            phonemes: w.phonemes,
          })),
        }).catch(err => {
          // Persistence is best-effort; never block UI on it.
          // eslint-disable-next-line no-console
          console.warn('[speaking] saveShadowingResults (word retry) failed', err);
        });
      }

      // Modal stays open: it switches to the result view via the
      // `wordPracticeResult` we set above. The learner dismisses
      // through 「完成」/X (handleCancelWordPractice) or retries
      // without closing through 「再試一次」 (handleRetryWordPractice).
    } catch (err) {
      console.error('Word assess failed:', err);
      setPronunciationError(t('practice.pronunciationCheckFailedRetry'));
    } finally {
      setIsAssessingWord(false);
      clearRecording();
    }
  };

  // Clear dictation state
  const clearDictationState = () => {
    setDictationResult(null);
    setShowDictationHint(false);
    setLockedLetters([]);
    setLetterInputs([]);
    setLetterPickerResetKey(k => k + 1);
    setSentenceWordInputs([]);
    setSentenceActiveWordIdx(0);
    setSentenceDictationResult(null);
  };

  // Clear meaning state
  const clearMeaningState = () => {
    setMeaningSelected(null);
    setMeaningResult(null);
  };

  // Clear fill-blank state
  const clearFillBlankState = () => {
    setFillBlankInputs([]);
    setFillBlankResult(null);
    setFbLetters([]);
    setFbLockedLetters([]);
    setCurrentFbBlankIdx(0);
    setLetterPickerResetKey(k => k + 1);
  };

  // Trigger success burst (reusable)
  const triggerSuccessBurst = useCallback(() => {
    setShowSuccessBurst(true);
    setTimeout(() => setShowSuccessBurst(false), 1400);
  }, []);

  // Trigger shake animation on incorrect attempt
  const triggerShake = useCallback(() => {
    setShakeBoxes(true);
    setTimeout(() => setShakeBoxes(false), 350);
  }, []);

  // Clear all states when moving to next word
  const clearAllStates = (nextItemId?: string) => {
    clearPronunciationState();
    clearDictationState();
    clearMeaningState();
    clearFillBlankState();
    resetPronunciationAttempts();
    setHasAttemptedSpeaking(false);
    setImprovedWordIndices(new Set());
    setWordPracticeMode(null);
    setWordPhonetic(null);
    setWordAudioUrl(null);
    setIsPlayingWordAudio(false);
    setWordPracticeResult(null);
    lpHadErrorsRef.current = false;
    // Set stage for next item from pre-assigned map
    if (nextItemId && wordStageMap[nextItemId]) {
      setCurrentStage(wordStageMap[nextItemId]);
    }
    wordStartTimeRef.current = Date.now();
  };

  // Each word has one stage — completing it means word is done
  const advanceToNextStage = useCallback((_currentStageName: LearningStage, vocabId: string) => {
    setWordProgressMap(prev => ({
      ...prev,
      [vocabId]: { [_currentStageName]: true },
    }));
    return 'word_done' as const;
  }, []);

  // Calculate completed words (single assigned stage done)
  const completedWordsCount = Object.keys(wordProgressMap).length;

  // Handle speaking stage completion - advance to next stage
  const handleSpeakingComplete = useCallback(() => {
    const pItem = currentPracticeItem;
    if (!pItem) return;

    setWordProgressMap(prev => ({
      ...prev,
      [pItem.itemId]: {
        ...prev[pItem.itemId],
        speaking: true,
      },
    }));

    const result = advanceToNextStage('speaking', pItem.itemId);
    if (result === 'word_done') {
      handleWordComplete();
    }
    clearPronunciationState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPracticeItem, pronunciationAttempts, advanceToNextStage]);

  // Handle letter box submit (check if all letters are filled correctly)
  const handleLetterBoxSubmit = useCallback((latestInputs?: string[]) => {
    const pItem = currentPracticeItem;
    if (!pItem) return;

    const inputs = latestInputs || letterInputs;
    const expected = pItem.word.toLowerCase();
    const userWord = inputs.join('').toLowerCase();

    if (userWord === expected) {
      setDictationResult('correct');
      triggerSuccessBurst();
      const hadErrors = lockedLetters.some(l => l);
      setMasteryDelta(hadErrors ? 1 : 2);
      setTimeout(() => setMasteryDelta(null), 1400);
      setWordProgressMap(prev => ({
        ...prev,
        [pItem.itemId]: {
          ...prev[pItem.itemId],
          speaking: prev[pItem.itemId]?.speaking || false,
          dictation: true,
        },
      }));
    } else {
      setWordHadError(prev => ({ ...prev, [pItem.itemId]: true }));
      const locked: boolean[] = [];
      const newInputs: string[] = [];

      for (let i = 0; i < expected.length; i++) {
        const userChar = inputs[i]?.toLowerCase() || '';
        const expectedChar = expected[i].toLowerCase();

        if (userChar === expectedChar) {
          locked.push(true);
          newInputs.push(expectedChar);
        } else {
          locked.push(false);
          newInputs.push('');
        }
      }

      setLockedLetters(locked);
      setLetterInputs(newInputs);
      triggerShake();

      setTimeout(() => {
        const firstEmptyIndex = locked.findIndex(l => !l);
        if (firstEmptyIndex >= 0) {
          letterInputRefs.current[firstEmptyIndex]?.focus();
        }
      }, 50);
    }
  }, [currentPracticeItem, letterInputs, lockedLetters, triggerSuccessBurst, triggerShake]);

  // Handle letter box input change
  const handleLetterInputChange = useCallback((index: number, value: string) => {
    if (lockedLetters[index]) return; // Can't edit locked letters

    const newInputs = [...letterInputs];
    const char = value.slice(-1).toLowerCase(); // Take only the last character
    newInputs[index] = char;
    setLetterInputs(newInputs);

    if (char) {
      // Find next unlocked position
      let nextUnlocked = -1;
      for (let i = index + 1; i < letterInputs.length; i++) {
        if (!lockedLetters[i]) {
          nextUnlocked = i;
          break;
        }
      }

      if (nextUnlocked >= 0) {
        letterInputRefs.current[nextUnlocked]?.focus();
      } else {
        // All boxes filled — auto-check after short delay
        setTimeout(() => handleLetterBoxSubmit(newInputs), 50);
      }
    }
  }, [lockedLetters, letterInputs, handleLetterBoxSubmit]);

  // Handle backspace in letter box mode
  const handleLetterKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !letterInputs[index]) {
      // Move to previous unlocked input
      for (let i = index - 1; i >= 0; i--) {
        if (!lockedLetters[i]) {
          letterInputRefs.current[i]?.focus();
          break;
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleLetterBoxSubmit();
    }
  }, [letterInputs, lockedLetters, handleLetterBoxSubmit]);

  // Generic word completion — record result, update mastery, advance to next word
  // Accepts either a UserVocabulary (legacy) or uses currentPracticeItem
  const handleWordComplete = useCallback(async (item?: UserVocabulary) => {
    if (isSessionComplete) return;

    const pItem = currentPracticeItem;
    if (!pItem) return;

    const hadError = !!wordHadError[pItem.itemId];
    setSessionResults(prev => [...prev, {
      vocabularyId: pItem.itemId,
      success: !hadError,
    }]);

    // Submit review via appropriate endpoint
    try {
      if (pItem.type === 'knowledgePoint') {
        // KP review: quality 0-5 (SM-2 style)
        const quality = hadError ? 2 : 4;
        await apiClient.post(
          `/cefr-knowledge/user/${pItem.itemId}/review`,
          { quality },
        );
      } else if (pItem.type === 'pattern') {
        // Pattern review: quality 0-5 (SM-2 style)
        const quality = hadError ? 2 : 4;
        await creatorApiClient.post(
          `/learning/patterns/${pItem.itemId}/review`,
          { quality },
        );
      } else if (pItem.type === 'sentence') {
        // Sentence drill items already feed FSRS via the /learning/
        // dictation-results POST in handleSentenceDictationSubmit (and
        // shadowing-results for speaking, when wired). Sending the
        // segment UUID to the vocab-review endpoint hits a 500 because
        // it's not a vocabulary id.
      } else {
        // Vocabulary review via FSRS endpoint
        const success = !hadError;
        const hintUsed = hadError;
        const elapsedMs = Date.now() - wordStartTimeRef.current;
        const timeSpentSeconds = Math.min(Math.max(Math.round(elapsedMs / 1000), 1), 15);

        const reviewResponse = await creatorApiClient.post(
          `/learning/vocabulary/${pItem.itemId}/review`,
          { success, timeSpentSeconds, hintUsed },
        );

        if (reviewResponse.data?.xpEarned > 0) {
          showXp(reviewResponse.data.xpEarned, reviewResponse.data.leveledUp, reviewResponse.data.newLevel);
        }
      }
    } catch (err: any) {
      console.error('Failed to submit review:', err);
    }

    // Move to next item or complete session
    if (currentIndex < activeItems.length - 1) {
      const nextItem = activeItems[currentIndex + 1];
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
      clearAllStates(nextItem.itemId);
    } else {
      setIsSessionComplete(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, activeItems, isSessionComplete, wordHadError, showXp, wordStageMap, currentPracticeItem]);

  // Handle dictation complete - advance to next stage or complete word
  const handleDictationComplete = useCallback(async () => {
    if (isSessionComplete || !currentPracticeItem) return;
    const pItem = currentPracticeItem;

    setWordProgressMap(prev => ({
      ...prev,
      [pItem.itemId]: {
        ...prev[pItem.itemId],
        dictation: true,
      },
    }));

    const result = advanceToNextStage('dictation', pItem.itemId);
    if (result === 'word_done') {
      await handleWordComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPracticeItem, isSessionComplete, advanceToNextStage, handleWordComplete]);

  // Sentence dictation: per-word check using the same loose comparison as
  // course-level DictationPage (case-insensitive, ignores diacritics +
  // surrounding punctuation). Result is "correct" only if every inputable
  // word matches; otherwise we surface which words missed and let the
  // learner retry without skipping the sentence.
  //
  // POSTs the per-word breakdown to /v1/learning/dictation-results with
  // source='review-sentence' so the same backend pipeline (FSRS update,
  // dictation_attempts persistence) handles the result — just with the
  // discounted rating that sentence-drill surfaces get.
  const handleSentenceDictationSubmit = useCallback(() => {
    if (!currentPracticeItem || currentPracticeItem.type !== 'sentence') return;
    if (sentenceWords.length === 0) return;
    const allCorrect = sentenceWords.every((expected, idx) => {
      if (isPunctuationOnly(expected)) return true;
      const userWord = sentenceWordInputs[idx] || '';
      return isWordCorrectIgnoreCase(userWord, expected);
    });

    // Fire-and-forget per-word results to the dictation pipeline. Don't
    // block the UI on the response — the result is local; the backend
    // call is for FSRS scheduling and analytics.
    if (currentPracticeItem.sentenceCourseId && currentPracticeItem.sentenceIndex != null) {
      const wordResults = sentenceWords
        .map((word, idx) => ({
          word,
          userInput: sentenceWordInputs[idx] || '',
          isCorrect: isPunctuationOnly(word)
            ? true
            : isWordCorrectIgnoreCase(sentenceWordInputs[idx] || '', word),
          sentenceIndex: currentPracticeItem.sentenceIndex!,
        }))
        .filter(r => !isPunctuationOnly(r.word));
      // dictation-results lives under the creator/learning API
      // (/v1/learning/...), not the public /api/v1 root that apiClient
      // points at. Wrong client → 404 + axios timeout for the user.
      creatorApiClient
        .post('/learning/dictation-results', {
          courseId: currentPracticeItem.sentenceCourseId,
          inputMode: 'typing',
          source: 'review-sentence',
          results: wordResults,
        })
        .catch((err) => {
          console.warn('[review/sentence] dictation-results POST failed:', err);
        });
    }

    if (allCorrect) {
      setSentenceDictationResult('correct');
      triggerSuccessBurst();
      setMasteryDelta(2);
      setTimeout(() => setMasteryDelta(null), 1400);
    } else {
      setSentenceDictationResult('incorrect');
      setWordHadError(prev => ({ ...prev, [currentPracticeItem.itemId]: true }));
      triggerShake();
    }
  }, [currentPracticeItem, sentenceWords, sentenceWordInputs, triggerSuccessBurst, triggerShake]);

  // Handle meaning stage complete
  const handleMeaningComplete = useCallback(async () => {
    if (isSessionComplete || !currentPracticeItem) return;
    const pItem = currentPracticeItem;

    setWordProgressMap(prev => ({
      ...prev,
      [pItem.itemId]: {
        ...prev[pItem.itemId],
        meaning: true,
      },
    }));

    const result = advanceToNextStage('meaning', pItem.itemId);
    if (result === 'word_done') {
      await handleWordComplete();
    }
    clearMeaningState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPracticeItem, isSessionComplete, advanceToNextStage, handleWordComplete]);

  // Handle fill-blank answer check — per-blank, like dictation
  const handleFillBlankCheck = useCallback((latestLetters?: string[][]) => {
    const pItem = currentPracticeItem;
    if (!pItem) return;
    const fb = fillBlankData[pItem.itemId];
    if (!fb) return;

    const letters = latestLetters || fbLetters;
    const bi = currentFbBlankIdx;
    const userWord = (letters[bi] || []).join('').toLowerCase();
    const expected = fb.answers[bi].toLowerCase();

    if (userWord === expected) {
      // This blank is correct — lock all its letters
      const newLetters = letters.map((w, i) => i === bi ? expected.split('') : [...w]);
      const newLocked = fbLockedLetters.map((w, i) => i === bi ? new Array(w.length).fill(true) : [...w]);
      setFbLetters(newLetters);
      setFbLockedLetters(newLocked);
      setFillBlankResult(null);

      if (bi < fb.answers.length - 1) {
        // Advance to next blank
        setCurrentFbBlankIdx(bi + 1);
        setTimeout(() => fillBlankInputRefs.current[0]?.focus(), 50);
      } else {
        // All blanks done!
        setFillBlankResult('correct');
        triggerSuccessBurst();
        setMasteryDelta(2);
        setTimeout(() => setMasteryDelta(null), 1400);
      }
    } else {
      setFillBlankResult('incorrect');
      setWordHadError(prev => ({ ...prev, [pItem.itemId]: true }));
      triggerShake();
      // Lock correct letters for current blank only
      const newLocked = fbLockedLetters.map((w, i) => {
        if (i !== bi) return [...w];
        return expected.split('').map((char, ci) =>
          (letters[bi]?.[ci] || '').toLowerCase() === char
        );
      });
      const newLetters = letters.map((w, i) => {
        if (i !== bi) return [...w];
        return expected.split('').map((char, ci) => {
          const userChar = letters[bi]?.[ci] || '';
          return userChar.toLowerCase() === char ? char : '';
        });
      });
      setFbLockedLetters(newLocked);
      setFbLetters(newLetters);

      // Focus first unlocked in current blank
      setTimeout(() => {
        const firstUnlocked = newLocked[bi].findIndex(l => !l);
        if (firstUnlocked >= 0) {
          fillBlankInputRefs.current[firstUnlocked]?.focus();
        }
      }, 50);
    }
  }, [currentPracticeItem, fillBlankData, fbLetters, fbLockedLetters, currentFbBlankIdx, triggerSuccessBurst, triggerShake]);

  // Handle fill-blank stage complete
  const handleFillBlankComplete = useCallback(async () => {
    if (isSessionComplete || !currentPracticeItem) return;
    const pItem = currentPracticeItem;

    setWordProgressMap(prev => ({
      ...prev,
      [pItem.itemId]: {
        ...prev[pItem.itemId],
        fillBlank: true,
      },
    }));

    const result = advanceToNextStage('fillBlank', pItem.itemId);
    if (result === 'word_done') {
      await handleWordComplete();
    }
    clearFillBlankState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPracticeItem, isSessionComplete, advanceToNextStage, handleWordComplete]);

  // LetterPicker bridge: dictation word complete
  const handleDictationLetterPickerComplete = useCallback(() => {
    const hadErrors = lpHadErrorsRef.current;
    setDictationResult('correct');
    triggerSuccessBurst();
    setMasteryDelta(hadErrors ? 1 : 2);
    setTimeout(() => setMasteryDelta(null), 1400);
    if (hadErrors && currentPracticeItem) {
      setWordHadError(prev => ({ ...prev, [currentPracticeItem.itemId]: true }));
    }
  }, [triggerSuccessBurst, currentPracticeItem]);

  // LetterPicker bridge: fill-blank per-blank complete
  const handleFbLetterPickerComplete = useCallback(() => {
    const pItem = currentPracticeItem;
    if (!pItem) return;
    const fb = fillBlankData[pItem.itemId];
    if (!fb) return;

    const bi = currentFbBlankIdx;
    if (bi < fb.answers.length - 1) {
      setCurrentFbBlankIdx(bi + 1);
      setLetterPickerResetKey(k => k + 1);
    } else {
      const hadErrors = lpHadErrorsRef.current;
      setFillBlankResult('correct');
      triggerSuccessBurst();
      setMasteryDelta(hadErrors ? 1 : 2);
      setTimeout(() => setMasteryDelta(null), 1400);
      if (hadErrors) {
        setWordHadError(prev => ({ ...prev, [pItem.itemId]: true }));
      }
    }
  }, [currentPracticeItem, fillBlankData, currentFbBlankIdx, triggerSuccessBurst]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (loading || error || isSessionComplete) return;

      if (e.key === 'Enter') {
        e.preventDefault();

        // Speaking stage — Enter always drives the primary forward
        // action: stop the take while recording, advance once a result
        // is on screen (regardless of correct/incorrect — the CTA row
        // exposes 再試一次 for explicit retry), or kick off a fresh
        // recording when nothing is in flight.
        if (currentStage === 'speaking') {
          if (isRecording) {
            stopRecording();
          } else if (pronunciationResult) {
            handleSpeakingComplete();
          } else if (!isCheckingPronunciation) {
            setPronunciationResult(null);
            setPronunciationError(null);
            startRecording();
          }
        }

        // Dictation stage — supports both word-level (letter boxes) and
        // sentence-level (per-word inputs). Sentence dictation owns its
        // own correct-state via sentenceDictationResult; without this
        // branch Enter on the green "下一題" button does nothing because
        // the per-input onKeyDown lives inside disabled inputs.
        //
        // No cooldown needed: the submit Enter and the advance Enter are
        // distinct presses. The submit press fires through the input's
        // onKeyDown and sets `sentenceDictationResult='correct'`, but the
        // window listener for that same press still sees the pre-commit
        // closure value (null/'incorrect'), so it can't double-fire. Only
        // a subsequent Enter sees the new 'correct' state and advances.
        if (currentStage === 'dictation') {
          if (dictationResult === 'correct') {
            handleDictationComplete();
          } else if (sentenceDictationResult === 'correct') {
            handleDictationComplete();
          }
        }

        // Meaning stage
        if (currentStage === 'meaning') {
          if (meaningResult) {
            handleMeaningComplete();
          }
        }

        // FillBlank stage
        if (currentStage === 'fillBlank') {
          if (fillBlankResult === 'correct') {
            handleFillBlankComplete();
          }
        }
        return;
      }

      // Spacebar: play audio in speaking and dictation stages
      if (e.key === ' ' && (currentStage === 'speaking' || currentStage === 'dictation')) {
        e.preventDefault();
        if (currentPracticeItem) {
          playPronunciation(currentPracticeItem.word);
        }
        return;
      }

      // Number keys 1-4 for meaning quiz options
      if (currentStage === 'meaning' && !meaningResult && ['1', '2', '3', '4'].includes(e.key)) {
        if (!currentPracticeItem) return;
        const quiz = meaningData[currentPracticeItem.itemId];
        if (!quiz?.shuffledOptions) return;
        const optIdx = parseInt(e.key) - 1;
        if (optIdx >= quiz.shuffledOptions.length) return;

        const option = quiz.shuffledOptions[optIdx];
        const isEn2Native = quiz.direction === 'en2native';
        const targetCorrect = isEn2Native ? quiz.correctDefinition : quiz.word;

        setMeaningSelected(option);
        if (option === targetCorrect) {
          setMeaningResult('correct');
          triggerSuccessBurst();
          setMasteryDelta(2);
          setTimeout(() => setMasteryDelta(null), 1400);
        } else {
          setMeaningResult('incorrect');
          setWordHadError(prev => ({ ...prev, [currentPracticeItem.itemId]: true }));
          setMasteryDelta(1);
          setTimeout(() => setMasteryDelta(null), 1400);
        }
        return;
      }

      if (e.key === 'Escape') {
        navigate(isDailyReview ? '/review' : '/vocabulary-book');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, isSessionComplete, navigate, currentStage, currentIndex, vocabularyList, meaningData, meaningResult, dictationResult, sentenceDictationResult, handleDictationComplete, handleMeaningComplete, fillBlankResult, handleFillBlankComplete, pronunciationResult, isRecording, isCheckingPronunciation, handleSpeakingComplete, startRecording, stopRecording, triggerSuccessBurst]);

  // Auto-skip: if assigned stage has no data, try another stage; if none work, skip word
  useEffect(() => {
    if (loading || materialsLoading || isSessionComplete || activeItems.length === 0) return;
    const pItem = activeItems[currentIndex];
    if (!pItem) return;
    const itemId = pItem.itemId;

    // KP/pattern can't do speaking/dictation. Sentence drill items can ONLY
    // do speaking/dictation — there's no meaning/fillBlank for a full
    // sentence in this surface.
    let availableStages: LearningStage[];
    if (pItem.type === 'knowledgePoint' || pItem.type === 'pattern') {
      availableStages = activeStages.filter(s => s === 'meaning' || s === 'fillBlank');
    } else if (pItem.type === 'sentence') {
      availableStages = activeStages.filter(s => s === 'speaking' || s === 'dictation');
    } else {
      availableStages = activeStages;
    }

    const stageHasData = (stage: LearningStage): boolean => {
      if ((pItem.type === 'knowledgePoint' || pItem.type === 'pattern') && (stage === 'speaking' || stage === 'dictation')) return false;
      if (pItem.type === 'sentence' && (stage === 'meaning' || stage === 'fillBlank')) return false;
      if (stage === 'speaking' || stage === 'dictation') return true;
      if (stage === 'meaning') return !!meaningData[itemId];
      if (stage === 'fillBlank') return !!fillBlankData[itemId];
      return true;
    };

    if (!stageHasData(currentStage)) {
      const fallback = availableStages.find(s => s !== currentStage && stageHasData(s));
      if (fallback) {
        setCurrentStage(fallback);
      } else {
        if (currentIndex < activeItems.length - 1) {
          const nextItem = activeItems[currentIndex + 1];
          setCurrentIndex(prev => prev + 1);
          setIsFlipped(false);
          clearAllStates(nextItem.itemId);
        } else {
          setIsSessionComplete(true);
        }
      }
    }
  }, [currentStage, currentIndex, loading, materialsLoading, isSessionComplete, activeItems, meaningData, fillBlankData, activeStages, wordStageMap]);

  // Play pronunciation
  // NOTE: Reuse a single Audio element to avoid mobile autoplay restrictions.
  // On iOS Safari, creating new Audio() after an async gap loses the user gesture
  // context, causing play() to be silently rejected.
  //
  // playingAudio state is driven by the audio element's actual events
  // (onplay / onended / onerror) rather than optimistically toggled —
  // optimistic toggles can race with the dictation init effect re-firing
  // (deps include sentenceWordInputs.length, which changes mid-run) and
  // leave the icon out of sync with the real playback state.
  const wireAudioStateBindings = (audio: HTMLAudioElement) => {
    audio.onplay = () => setPlayingAudio(true);
    audio.onended = () => setPlayingAudio(false);
    audio.onerror = () => setPlayingAudio(false);
    // onpause fires both when audio finishes naturally AND when we
    // call .pause() before swapping src — covering it here keeps the
    // icon in sync during sentence transitions.
    audio.onpause = () => setPlayingAudio(false);
  };

  const playPronunciation = async (word: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Sentence drill items carry their own segment audio URL. Bypass the
    // dictionary TTS pipeline (which can't pronounce a full sentence) and
    // play the source recording directly.
    if (currentPracticeItem?.type === 'sentence' && currentPracticeItem.sentenceAudioUrl) {
      const url = resolveMediaUrl(currentPracticeItem.sentenceAudioUrl);
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      const audio = audioRef.current;
      wireAudioStateBindings(audio);
      audio.src = url;
      audio.play().catch(() => setPlayingAudio(false));
      return;
    }

    try {
      const data = await dictionaryService.lookup(word, { generateAudio: true });
      if (data.audio && data.audio.length > 0) {
        const audioUrl = data.audio[0].url;
        if (audioUrl) {
          const url = resolveMediaUrl(audioUrl);
          // Reuse existing element or create one — setting src on an existing
          // element is more permissive on mobile than new Audio(url) after await.
          if (!audioRef.current) {
            audioRef.current = new Audio();
          }
          const audio = audioRef.current;
          wireAudioStateBindings(audio);
          audio.src = url;
          audio.play().catch(() => setPlayingAudio(false));
        } else {
          setPlayingAudio(false);
        }
      } else {
        setPlayingAudio(false);
      }
    } catch (err) {
      console.error('Failed to play pronunciation:', err);
      setPlayingAudio(false);
    }
  };

  // Calculate session stats
  const getSessionStats = () => {
    const correct = sessionResults.filter(r => r.success).length;
    const incorrect = sessionResults.length - correct;
    const accuracy = sessionResults.length > 0
      ? Math.round((correct / sessionResults.length) * 100)
      : 0;
    return { correct, incorrect, accuracy };
  };

  // Restart session - reload to get updated mastery levels
  const handleRestart = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setSessionResults([]);
    setIsSessionComplete(false);
    setWordProgressMap({});
    setWordHadError({});
    clearAllStates();
    // Reload from API to get fresh mastery levels and re-apply smart selection
    loadVocabulary();
  };

  // Review wrong answers only
  const handleReviewWrong = () => {
    const wrongIds = new Set(sessionResults
      .filter(r => !r.success)
      .map(r => r.vocabularyId));

    const wrongItems = activeItems.filter(pi => wrongIds.has(pi.itemId));

    if (wrongItems.length > 0) {
      const shuffled = wrongItems.sort(() => Math.random() - 0.5);
      const quizOnlyStages: LearningStage[] = activeStages.filter(s => s === 'meaning' || s === 'fillBlank');
      const stageMap: Record<string, LearningStage> = {};
      for (const item of shuffled) {
        if (item.type === 'knowledgePoint' || item.type === 'pattern') {
          stageMap[item.itemId] = quizOnlyStages.length > 0 ? quizOnlyStages[Math.floor(Math.random() * quizOnlyStages.length)] : 'meaning';
        } else {
          stageMap[item.itemId] = activeStages[Math.floor(Math.random() * activeStages.length)];
        }
      }
      setWordStageMap(stageMap);
      // Rebuild vocabularyList from wrong vocab items only
      const wrongVocabItems = shuffled.filter(pi => pi.type === 'vocabulary' && pi.userVocabulary).map(pi => pi.userVocabulary!);
      setVocabularyList(wrongVocabItems);
      setPracticeItems(shuffled);
      setCurrentIndex(0);
      setIsFlipped(false);
      setSessionResults([]);
      setIsSessionComplete(false);
      setWordProgressMap({});
      setWordHadError({});
      clearAllStates(shuffled[0].itemId);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flashcard-page">
        <div className="flashcard-loading">
          <div className="loading-spinner"></div>
          <p>{t('practice.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flashcard-page">
        <div className="flashcard-error">
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={loadVocabulary} className="btn-retry">{t('practice.retry')}</button>
            <button onClick={() => navigate(isDailyReview ? '/review' : '/vocabulary-book')} className="btn-back">{t(isDailyReview ? 'practice.backToReview' : 'practice.backToVocabulary')}</button>
          </div>
        </div>
      </div>
    );
  }

  // Session complete
  if (isSessionComplete) {
    const stats = getSessionStats();
    return (
      <div className="flashcard-page">
        <div className="session-complete">
          <div className="complete-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
          <h2>{t('practice.practiceComplete')}</h2>

          <div className="session-stats">
            <div className="stat-card stat-correct">
              <span className="stat-number">{stats.correct}</span>
              <span className="stat-label">{t('practice.remembered')}</span>
            </div>
            <div className="stat-card stat-incorrect">
              <span className="stat-number">{stats.incorrect}</span>
              <span className="stat-label">{t('practice.forgotten')}</span>
            </div>
            <div className="stat-card stat-accuracy">
              <span className="stat-number">{stats.accuracy}%</span>
              <span className="stat-label">{t('practice.accuracy')}</span>
            </div>
          </div>

          <PhonicsSessionSummary errors={getSessionErrors()} />

          <div className="complete-actions">
            <button onClick={handleRestart} className="btn-primary">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
              {t('practice.practiceAgain')}
            </button>
            {stats.incorrect > 0 && (
              <button onClick={handleReviewWrong} className="btn-secondary">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z"/>
                </svg>
                {t('practice.practiceIncorrectOnly', { count: stats.incorrect })}
              </button>
            )}
            <button onClick={() => navigate(isDailyReview ? '/review' : '/vocabulary-book')} className="btn-outline">
              {t(isDailyReview ? 'practice.backToReview' : 'practice.backToVocabulary')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pItem = currentPracticeItem; // unified current item

  return (
    <div className="flashcard-page">
      {/* Header */}
      <div className="flashcard-header two-stage-header">
        <button className="btn-close" onClick={() => navigate(isDailyReview ? '/review' : '/vocabulary-book')} title={t('practice.closeTitle')}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
        {/* Progress bar integrated with counter */}
        <div className="flashcard-progress-section">
          <div className="flashcard-progress-bar">
            <div className="flashcard-progress-fill" style={{ width: `${(completedWordsCount / activeItems.length) * 100}%` }}></div>
          </div>
          <span className="flashcard-progress-text">{currentIndex + 1} / {activeItems.length}</span>
        </div>
        {/* Input mode toggle for dictation/fillBlank stages */}
        {(currentStage === 'dictation' || currentStage === 'fillBlank') ? (
          <InputModeToggle isLetterPicker={isLetterPicker} onToggle={() => { toggleInputMode(); setLetterPickerResetKey(k => k + 1); }} />
        ) : (
          <div style={{ width: 40 }}></div>
        )}
      </div>

      {/* Mastery Delta Animation (per-stage, consistent with course learning modes) */}
      {masteryDelta !== null && (
        <div className={`mastery-delta ${masteryDelta > 0 ? 'positive' : 'negative'}`}>
          {masteryDelta > 0 ? `+${masteryDelta}` : masteryDelta}
        </div>
      )}

      {/* XP Toast (consistent with course learning modes) */}
      <XpToastContainer toasts={xpToasts} />

      {/* Word-practice overlay — shared with ShadowingPage. */}
      {wordPracticeMode && (
        <WordPracticeModal
          word={wordPracticeMode.word}
          originalScore={wordPracticeMode.originalScore}
          phonetic={wordPhonetic}
          audioUrl={wordAudioUrl}
          isRecording={isRecording}
          isAssessing={isAssessingWord}
          isPlayingAudio={isPlayingWordAudio}
          result={wordPracticeResult ?? undefined}
          onStartRecord={startRecording}
          onStopRecord={stopRecording}
          onPlayAudio={handlePlayWordAudio}
          onRetry={handleRetryWordPractice}
          onCancel={handleCancelWordPractice}
        />
      )}

      {/* Flashcard Container - Two Stage Mode */}
      <div className="flashcard-container two-stage-mode">
        <div className="flashcard-card">
          {/* Speaking Stage */}
          {currentStage === 'speaking' && (
            <div className={`stage-content speaking-stage${pItem?.type === 'sentence' ? ' sentence-speaking-stage' : ''}`}>
              <div className="stage-label">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                </svg>
                <span>{pItem?.type === 'sentence'
                  ? t('practice.speakSentence', { defaultValue: '跟讀整句' })
                  : t('practice.speakWord')}</span>
              </div>

              {pItem?.type === 'sentence' && pItem.isErrorSentence && (
                <p className="sentence-drill-tag" style={{ color: '#dc2626', fontSize: '0.8rem', margin: '4px 0' }}>
                  {t('practice.sentenceErrorReview', { defaultValue: '⟳ 之前答錯的句子' })}
                </p>
              )}

              <h2
                className="word"
                style={pItem?.type === 'sentence'
                  ? { fontSize: '1.3rem', lineHeight: 1.45, padding: '0 12px', maxWidth: 640 }
                  : undefined
                }
              >
                {/*
                  After the learner has produced a take for this sentence,
                  swap the headline into authored breath-group form so they
                  can see where to pause on retry. Stays chunked across
                  retries; resets when moving to the next item.
                  Falls back to plain text if the segment has no
                  speaking_strategy.chunks authored.
                */}
                {pItem?.type === 'sentence'
                  && hasAttemptedSpeaking
                  && pItem.sentenceChunks
                  && pItem.sentenceChunks.length > 0
                  ? pItem.sentenceChunks.map((chunk, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="sentence-chunk-sep" aria-hidden="true"> / </span>}
                        <span className="sentence-chunk">{chunk}</span>
                      </React.Fragment>
                    ))
                  : pItem?.word}
              </h2>

              {pItem?.type === 'sentence' && pItem.sentenceTranslation && (
                <p className="sentence-translation" style={{ color: '#666', fontSize: '0.9rem', marginTop: 4, padding: '0 12px', maxWidth: 640 }}>
                  {pItem.sentenceTranslation}
                </p>
              )}

              {/* Show pronunciation when user got it wrong */}
              {pronunciationResult && !pronunciationResult.isCorrect && pItem?.pronunciation && (
                <p className="pronunciation">/{convertPhonetic(pItem.pronunciation, phoneticSystem)}/</p>
              )}

              {/* Play pronunciation button */}
              <div className="speaking-action-row">
                <button
                  className={`audio-btn ${playingAudio ? 'playing' : ''}`}
                  onClick={() => pItem && playPronunciation(pItem.word)}
                  title={t('practice.playPronunciation')}
                >
                  {playingAudio ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                  )}
                </button>
                <kbd className="kbd-hint kbd-hint-muted">Space</kbd>
              </div>

              {/* Recording section */}
              <div className="pronunciation-section">
                {(recordingError || pronunciationError) && (
                  <div className="pronunciation-error">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    <span>{recordingError || pronunciationError}</span>
                  </div>
                )}

                {isCheckingPronunciation ? (
                  <div className="checking-status">
                    <div className="checking-spinner"></div>
                    <span>{t('practice.checking')}</span>
                  </div>
                ) : pronunciationResult ? (
                  <>
                    {/* Detailed verdict — full score panel + per-word
                        breakdown + Loopy Coach. The old LoopyVerdict
                        bubble was redundant on top of this and its
                        binary "correct/incorrect" verdict contradicted
                        scores like 96/100 with one mispronounced word
                        (bubble said 再試一次 while coach praised the
                        same attempt). The CTA row below replaces it. */}
                    <PronunciationResultPanel
                      result={{
                        overallScore: pronunciationResult.overallScore,
                        accuracyScore: pronunciationResult.accuracyScore,
                        fluencyScore: pronunciationResult.fluencyScore,
                        completenessScore: pronunciationResult.completenessScore,
                        prosodyScore: pronunciationResult.prosodyScore,
                        words: pronunciationResult.words,
                        transcribedText: pronunciationResult.transcribedText,
                        expectedText: pronunciationResult.expectedWord,
                      }}
                      onWordPractice={handleWordPractice}
                      improvedWordIndices={improvedWordIndices}
                      practiceLocked={!!wordPracticeMode}
                    />
                    {pronunciationResult.coach && (
                      <CoachingPanel
                        result={pronunciationResult.coach}
                        score={pronunciationResult.overallScore}
                      />
                    )}
                    {/* Hide the bottom CTA row while the word-practice
                        modal is up — the modal owns the recorder during
                        that flow, so a Retry / Next pair underneath would
                        compete with the modal's Stop / Cancel buttons. */}
                    {!wordPracticeMode && (
                      <div className="speaking-cta-row">
                        <button
                          type="button"
                          className="speaking-cta-btn speaking-cta-btn--retry"
                          onClick={() => {
                            clearPronunciationState();
                            setTimeout(() => startRecording(), 100);
                          }}
                        >
                          {t('practice.tryAgain', { defaultValue: '再試一次' })}
                        </button>
                        <button
                          type="button"
                          className="speaking-cta-btn speaking-cta-btn--next"
                          onClick={handleSpeakingComplete}
                        >
                          {t('practice.nextSentence', { defaultValue: '下一句' })}
                          <kbd className="speaking-cta-kbd">Enter</kbd>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="speaking-action-row">
                    <button
                      className={`record-toggle-btn ${isRecording ? 'recording' : ''}`}
                      onClick={() => {
                        if (isRecording) {
                          stopRecording();
                        } else {
                          setPronunciationResult(null);
                          setPronunciationError(null);
                          startRecording();
                        }
                      }}
                    >
                      {isRecording ? (
                        <>
                          <span className="recording-indicator"></span>
                          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                            <path d="M6 6h12v12H6z"/>
                          </svg>
                          <span>{t('practice.stopRecording')}</span>
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                          </svg>
                          <span>{t('practice.recordPronunciation')}</span>
                        </>
                      )}
                    </button>
                    <kbd className="kbd-hint kbd-hint-muted">Enter</kbd>
                  </div>
                )}
              </div>

              {!isRecording && !isCheckingPronunciation && !pronunciationResult && (
                <p className="stage-hint">{t('practice.recordHint')}</p>
              )}
            </div>
          )}

          {/* Dictation Stage */}
          {currentStage === 'dictation' && pItem?.type === 'sentence' && (
            <div className="stage-content dictation-stage sentence-dictation-stage">
              <div className="stage-label">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
                <span>{t('practice.dictateSentence', { defaultValue: '聽寫整句' })}</span>
              </div>

              {pItem.isErrorSentence && (
                <p className="sentence-drill-tag" style={{ color: '#dc2626', fontSize: '0.8rem', margin: '4px 0' }}>
                  {t('practice.sentenceErrorReview', { defaultValue: '⟳ 之前答錯的句子' })}
                </p>
              )}

              <button
                className={`dictation-audio-btn ${playingAudio ? 'playing' : ''}`}
                onClick={() => playPronunciation(pItem.word)}
                title={t('practice.playPronunciation')}
                style={{ marginBottom: 12 }}
              >
                {playingAudio ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
              </button>
              <p className="dictation-instruction">
                {t('practice.listenAndTypeSentence', { defaultValue: '聽完播放後輸入整句' })}
                <kbd className="kbd-hint kbd-hint-muted">Space</kbd>
              </p>

              {/* Per-word inputs via shared <WordInputsArea>. Always
                  rendered — even on a fully-correct submit we keep it
                  visible (each word turns green) so the moment of
                  success isn't snapped past. The action row below
                  swaps in the appropriate button(s). */}
              <WordInputsArea
                correctWords={sentenceWords}
                wordInputs={sentenceWordInputs}
                activeWordIndex={sentenceActiveWordIdx}
                showResult={sentenceDictationResult !== null}
                shake={shakeBoxes}
                inputRefs={sentenceWordRefs}
                onInputChange={(idx, value) => {
                  setSentenceWordInputs(prev => {
                    const next = [...prev];
                    next[idx] = value;
                    return next;
                  });
                  if (sentenceDictationResult) setSentenceDictationResult(null);
                }}
                onActiveChange={setSentenceActiveWordIdx}
                onSubmit={handleSentenceDictationSubmit}
                onPlayAudio={() => playPronunciation(pItem.word)}
              />
              {sentenceDictationResult === 'correct' && pItem.sentenceTranslation && (
                <p className="dictation-correct-def" style={{ marginTop: 12, color: '#475569', fontSize: '0.95rem' }}>
                  {pItem.sentenceTranslation}
                </p>
              )}
              <div className="letter-box-actions" style={{ marginTop: 16 }}>
                {sentenceDictationResult === 'correct' ? (
                  <button className="dictation-submit-btn success" onClick={handleDictationComplete}>
                    {t('practice.next')}<kbd className="kbd-hint">Enter</kbd>
                  </button>
                ) : sentenceDictationResult === 'incorrect' ? (
                  <>
                    <button
                      className="dictation-submit-btn"
                      onClick={() => {
                        setSentenceDictationResult(null);
                        setSentenceWordInputs(new Array(sentenceWords.length).fill(''));
                        const firstInputable = sentenceWords.findIndex(w => /[a-zA-Z0-9]/.test(w));
                        setSentenceActiveWordIdx(firstInputable >= 0 ? firstInputable : 0);
                      }}
                    >
                      {t('practice.tryAgain', { defaultValue: '重試' })}
                    </button>
                    <button
                      className="dictation-submit-btn success"
                      onClick={handleDictationComplete}
                      style={{ marginLeft: 8 }}
                    >
                      {t('practice.next')}<kbd className="kbd-hint">Enter</kbd>
                    </button>
                  </>
                ) : (
                  <button
                    className={`dictation-submit-btn${sentenceWordInputs.every(w => !w) ? ' disabled' : ''}`}
                    onClick={handleSentenceDictationSubmit}
                    disabled={sentenceWordInputs.every(w => !w)}
                  >
                    {t('practice.confirm')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Dictation Stage — vocabulary/word level (letter boxes) */}
          {currentStage === 'dictation' && pItem?.type !== 'sentence' && (
            <div className="stage-content dictation-stage">
              <div className="stage-label">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
                <span>{t('practice.dictateWord')}</span>
              </div>

              {dictationResult === 'correct' ? (
                /* ── Correct: show vocabulary card ── */
                <div className="dictation-correct-card">
                  <h2 className="dictation-correct-word">{pItem?.word}</h2>
                  {pItem?.pronunciation && (
                    <p className="dictation-correct-pron">
                      /{convertPhonetic(pItem.pronunciation, phoneticSystem)}/
                    </p>
                  )}
                  <button
                    className={`audio-btn ${playingAudio ? 'playing' : ''}`}
                    onClick={() => pItem && playPronunciation(pItem.word)}
                    title={t('practice.playPronunciation')}
                  >
                    {playingAudio ? (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                      </svg>
                    )}
                  </button>
                  {/* Definition — prefer localized, fallback zh-CN↔zh-TW, then English */}
                  {(() => {
                    const locale = localStorage.getItem('i18nextLng') || 'zh-TW';
                    const defs = pItem?.definitions;
                    const def = getCleanLocalizedDefinition(defs, locale, pItem?.definition ?? undefined);
                    return def ? <p className="dictation-correct-def">{def}</p> : null;
                  })()}
                  {pItem?.notes && (
                    <p className="dictation-correct-notes">{pItem.notes}</p>
                  )}
                  <button className="dictation-submit-btn success" onClick={handleDictationComplete}>
                    {t('practice.next')}<kbd className="kbd-hint">Enter</kbd>
                  </button>
                </div>
              ) : (
                /* ── Input mode: audio button + letter boxes ── */
                <>
                  <button
                    className={`dictation-audio-btn ${playingAudio ? 'playing' : ''}`}
                    onClick={() => pItem && playPronunciation(pItem.word)}
                    title={t('practice.playPronunciation')}
                  >
                    {playingAudio ? (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                      </svg>
                    )}
                  </button>
                  <p className="dictation-instruction">{t('practice.listenAndType')}<kbd className="kbd-hint kbd-hint-muted">Space</kbd></p>

                  {isLetterPicker ? (
                    <LetterPicker
                      expectedText={pItem?.word || ''}
                      onComplete={handleDictationLetterPickerComplete}
                      onErrorData={handleLpErrorData}
                      onLetterError={handleLetterError}
                      resetKey={letterPickerResetKey}
                    />
                  ) : (() => {
                    const word = pItem?.word || '';
                    const boxSize = word.length > 10 ? 'sm' : word.length > 7 ? 'md' : 'lg';
                    return (
                      <>
                        <div className={`letter-boxes-container fb-boxes-${boxSize}${shakeBoxes ? ' shake' : ''}`}>
                          {letterInputs.map((letter, index) => (
                            lockedLetters[index] ? (
                              letter === ' ' ? (
                                <span key={index} className="letter-box-spacer" />
                              ) : (
                                <span key={index} className="letter-box locked">
                                  {letter}
                                </span>
                              )
                            ) : (
                              <input
                                key={index}
                                ref={(el) => { letterInputRefs.current[index] = el; }}
                                type="text"
                                autoComplete="off"
                                className="letter-box input"
                                value={letter}
                                onChange={(e) => handleLetterInputChange(index, e.target.value)}
                                onKeyDown={(e) => handleLetterKeyDown(index, e)}
                                maxLength={2}
                                autoFocus={index === lockedLetters.findIndex(l => !l)}
                              />
                            )
                          ))}
                        </div>
                        <div className="letter-box-actions">
                          <button
                            className={`dictation-submit-btn${letterInputs.every((l, i) => !l && !lockedLetters[i]) ? ' disabled' : ''}`}
                            onClick={() => handleLetterBoxSubmit()}
                            disabled={letterInputs.every((l, i) => !l && !lockedLetters[i])}
                          >
                            {t('practice.confirm')}
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}

              {/* Hint display */}
              {showDictationHint && (
                <div className="dictation-hint">
                  <span className="hint-label">{t('practice.answer')}</span>
                  <span className="hint-word">{pItem?.word}</span>
                </div>
              )}
            </div>
          )}

          {/* Meaning Review Stage */}
          {currentStage === 'meaning' && (
            <div className="stage-content meaning-stage">
              <div className="stage-label">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z"/>
                </svg>
                <span>{t('practice.modeMeaning')}</span>
              </div>

              {(() => {
                const quiz = meaningData[pItem?.itemId || ''];
                if (!quiz) {
                  if (materialsLoading) {
                    return (
                      <div className="stage-loading">
                        <div className="stage-loading-spinner" />
                        <p>{t('practice.loadingMaterials', { defaultValue: '正在準備題目...' })}</p>
                      </div>
                    );
                  }
                  return (
                    <div className="fill-blank-unavailable">
                      <p>{t('practice.meaningUnavailable', { defaultValue: '此單字暫無字義測驗' })}</p>
                      <button className="next-btn" onClick={handleMeaningComplete}>
                        {t('practice.next')}<kbd className="kbd-hint">Enter</kbd>
                      </button>
                    </div>
                  );
                }

                const isEn2Native = quiz.direction === 'en2native';

                return (
                  <>
                    <p className="meaning-question">
                      {pItem?.type === 'pattern'
                        ? t('practice.meaningQuestionPattern', { grammar: quiz.grammarName || '' })
                        : isEn2Native ? t('practice.meaningQuestion') : t('practice.meaningQuestionReverse')}
                    </p>
                    <h2 className="word" style={pItem?.type === 'pattern' ? { fontSize: '1.3rem' } : undefined}>
                      {isEn2Native ? quiz.word : quiz.correctDefinition}
                    </h2>

                    <div className="meaning-options">
                      {(quiz.shuffledOptions || []).map((option, idx) => {
                        const isCorrect = isEn2Native
                          ? option === quiz.correctDefinition
                          : option === quiz.word;
                        const targetCorrect = isEn2Native ? quiz.correctDefinition : quiz.word;

                        let className = 'meaning-option-btn';
                        if (meaningResult && meaningSelected === option) {
                          className += option === targetCorrect ? ' correct' : ' incorrect';
                        } else if (meaningResult === 'incorrect' && option === targetCorrect) {
                          className += ' correct';
                        }

                        return (
                          <button
                            key={idx}
                            className={className}
                            onClick={() => {
                              if (meaningResult || !pItem) return;
                              setMeaningSelected(option);
                              if (option === targetCorrect) {
                                setMeaningResult('correct');
                                triggerSuccessBurst();
                                setMasteryDelta(2);
                                setTimeout(() => setMasteryDelta(null), 1400);
                              } else {
                                setMeaningResult('incorrect');
                                setWordHadError(prev => ({ ...prev, [pItem.itemId]: true }));
                                setMasteryDelta(1);
                                setTimeout(() => setMasteryDelta(null), 1400);
                              }
                            }}
                            disabled={meaningResult !== null}
                          >
                            <span className="option-number">{idx + 1}</span>
                            <span className="option-text">{option}</span>
                          </button>
                        );
                      })}
                    </div>

                    {meaningResult && (
                      <LoopyVerdict
                        kind={meaningResult === 'correct' ? 'correct' : 'incorrect'}
                        title={meaningResult === 'correct' ? t('practice.meaningCorrect') : t('practice.meaningIncorrect')}
                        nextLabel={t('practice.next')}
                        onNext={handleMeaningComplete}
                      />
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Fill-in-Blank Stage — Redesigned: sentence display + separate letter-box */}
          {currentStage === 'fillBlank' && (
            <div className="stage-content fill-blank-stage">
              <div className="stage-label">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
                <span>{t('practice.fillBlankTitle')}</span>
              </div>

              {(() => {
                const fb = fillBlankData[pItem?.itemId || ''];
                if (fillBlankLoading) {
                  return (
                    <div className="checking-status">
                      <div className="checking-spinner"></div>
                      <span>{t('practice.fillBlankLoading')}</span>
                    </div>
                  );
                }
                if (!fb) {
                  if (materialsLoading) {
                    return (
                      <div className="stage-loading">
                        <div className="stage-loading-spinner" />
                        <p>{t('practice.loadingMaterials', { defaultValue: '正在準備題目...' })}</p>
                      </div>
                    );
                  }
                  return (
                    <div className="fill-blank-unavailable">
                      <p>{t('practice.fillBlankUnavailable')}</p>
                      <button className="next-btn" onClick={handleFillBlankComplete}>
                        {t('practice.next')}<kbd className="kbd-hint">Enter</kbd>
                      </button>
                    </div>
                  );
                }

                const parts = fb.sentence.split('_____');
                const bi = currentFbBlankIdx;
                const currentAnswer = fb.answers[bi] || '';
                const currentLetters = fbLetters[bi] || [];
                const currentLocked = fbLockedLetters[bi] || [];
                // Adaptive letter-box size for long words
                const boxSize = currentAnswer.length > 10 ? 'sm' : currentAnswer.length > 7 ? 'md' : 'lg';

                return (
                  <>
                    {/* Chunk hint — only show translation before completion, reveal English chunk after */}
                    <div className="fill-blank-chunk-hint">
                      <span className="chunk-label">
                        {pItem?.type === 'knowledgePoint'
                          ? t('practice.fillBlankWord', '單字')
                          : pItem?.type === 'pattern'
                            ? t('practice.fillBlankPattern', '句型')
                            : t('practice.fillBlankChunk')}
                      </span>
                      {fillBlankResult === 'correct' && (
                        <span className="chunk-term">{fb.chunk}</span>
                      )}
                      <span className="chunk-translation">{fb.chunkTranslation}</span>
                    </div>

                    {/* Sentence display — highlight chunk after correct answer */}
                    <div className="fb-sentence-display">
                      {(() => {
                        if (fillBlankResult === 'correct') {
                          // Build completed sentence and highlight the chunk within it
                          let fullSentence = fb.sentence;
                          for (const ans of fb.answers) {
                            fullSentence = fullSentence.replace('_____', ans);
                          }
                          // Find chunk in sentence (case-insensitive) and wrap it
                          const chunkRegex = new RegExp(`(${fb.chunk.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'i');
                          const chunkParts = fullSentence.split(chunkRegex);
                          return chunkParts.map((seg, i) => (
                            chunkRegex.test(seg)
                              ? <span key={i} className="fb-chunk-highlight">{seg}</span>
                              : <span key={i}>{seg}</span>
                          ));
                        }
                        // Normal mode: show blanks
                        return parts.map((part, idx) => (
                          <React.Fragment key={idx}>
                            <span>{part}</span>
                            {idx < parts.length - 1 && (() => {
                              const ansLen = Math.max(fb.answers[idx].length, 3);
                              const slotStyle = { width: `${ansLen + 1}ch` };
                              return (
                                <span
                                  className={`fb-blank-slot ${
                                    idx < bi ? 'completed' : idx === bi ? 'active' : 'pending'
                                  }`}
                                  style={slotStyle}
                                >
                                  {idx < bi
                                    ? fb.answers[idx]
                                    : idx === bi
                                      ? (currentLetters.some(l => l) ? currentLetters.join('') : '')
                                      : ''
                                  }
                                </span>
                              );
                            })()}
                          </React.Fragment>
                        ));
                      })()}
                    </div>

                    {/* Blank progress dots */}
                    {fb.answers.length > 1 && (
                      <div className="fb-blank-progress">
                        {fb.answers.map((_, idx) => (
                          <span key={idx} className={`fb-progress-dot ${
                            idx < bi ? 'done' : idx === bi ? 'current' : ''
                          }`} />
                        ))}
                      </div>
                    )}

                    {/* Letter-box input area (dictation size) */}
                    {isLetterPicker && fillBlankResult !== 'correct' ? (
                      <div className="fb-letterbox-area">
                        <LetterPicker
                          expectedText={currentAnswer}
                          onComplete={handleFbLetterPickerComplete}
                          onErrorData={handleLpErrorData}
                          onLetterError={handleLetterError}
                          resetKey={letterPickerResetKey}
                        />
                      </div>
                    ) : !isLetterPicker ? (
                      <div className="fb-letterbox-area">
                        <div className={`letter-boxes-container fb-boxes-${boxSize}${shakeBoxes ? ' shake' : ''}`}>
                          {currentLetters.map((letter, charIdx) => {
                            if (currentLocked[charIdx]) {
                              return (
                                <span key={charIdx} className="letter-box locked">
                                  {letter}
                                </span>
                              );
                            }
                            return (
                              <input
                                key={charIdx}
                                ref={(el) => { fillBlankInputRefs.current[charIdx] = el; }}
                                type="text"
                                autoComplete="off"
                                className="letter-box input"
                                value={letter}
                                onChange={(e) => {
                                  if (fillBlankResult === 'correct') return;
                                  const char = e.target.value.slice(-1).toLowerCase();
                                  const newLetters = fbLetters.map(w => [...w]);
                                  newLetters[bi][charIdx] = char;
                                  setFbLetters(newLetters);
                                  setFillBlankResult(null);

                                  if (char) {
                                    // Auto-advance to next unlocked letter in current blank
                                    let nextIdx = -1;
                                    for (let c = charIdx + 1; c < currentLetters.length; c++) {
                                      if (!currentLocked[c]) { nextIdx = c; break; }
                                    }
                                    if (nextIdx !== -1) {
                                      setTimeout(() => fillBlankInputRefs.current[nextIdx]?.focus(), 0);
                                    } else {
                                      // All letters filled — auto-check this blank
                                      const allFilled = newLetters[bi].every((l, li) => l || currentLocked[li]);
                                      if (allFilled) {
                                        setTimeout(() => handleFillBlankCheck(newLetters), 50);
                                      }
                                    }
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleFillBlankCheck();
                                  } else if (e.key === 'Backspace' && !letter) {
                                    for (let c = charIdx - 1; c >= 0; c--) {
                                      if (!currentLocked[c]) {
                                        fillBlankInputRefs.current[c]?.focus();
                                        break;
                                      }
                                    }
                                  }
                                }}
                                maxLength={2}
                                disabled={fillBlankResult === 'correct'}
                                autoFocus={charIdx === currentLocked.findIndex((l, i) => !l && i >= 0)}
                              />
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    {/* Answer reveal (per-blank) */}
                    {fillBlankResult === 'incorrect' && (
                      <LoopyVerdict
                        kind="incorrect"
                        title={t('practice.fillBlankShowAnswer')}
                        message={<strong>{currentAnswer}</strong>}
                      />
                    )}

                    {/* Actions */}
                    <div className="fill-blank-actions">
                      {fillBlankResult === 'correct' ? (
                        <LoopyVerdict
                          kind="correct"
                          title={t('practice.meaningCorrect')}
                          nextLabel={t('practice.next')}
                          onNext={handleFillBlankComplete}
                        />
                      ) : (
                        !isLetterPicker && <button
                          className={`dictation-submit-btn ${currentLetters.every((l, i) => !l && !currentLocked[i]) ? 'disabled' : ''}`}
                          onClick={() => handleFillBlankCheck()}
                          disabled={currentLetters.every((l, i) => !l && !currentLocked[i])}
                        >
                          {t('practice.confirm')}
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          {/* Success Burst Overlay — reusable across all stages */}
          {showSuccessBurst && (
            <div className="success-burst-overlay">
              <div className="success-burst-circle" />
              <svg className="success-burst-check" viewBox="0 0 52 52">
                <circle cx="26" cy="26" r="24" fill="none" stroke="currentColor" strokeWidth="3" />
                <path d="M15 27l7 7 15-15" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlashcardPracticePage;
